import { useState, useEffect } from "react";
import TopBar from "../components/TopBar";
import Avatar from "../components/Avatar";
import Badge from "../components/Badge";
import FieldRow from "../components/FieldRow";
import Toast from "../components/Toast";
import { uid } from "../constants";
import { apiFetch } from "../utils/api";
import { todayIST } from "../utils/dateUtils";
import { loadWatermarkDataUrl, applyPdfWatermark } from "../utils/pdfWatermark";

// ── Column length limits — mirror the DB schema (clients table) ──────────────
// Keep in sync with controllers/clientController.js CLIENT_FIELD_MAX.
const CLIENT_FIELD_MAX = {
  code: 20, name: 160, legalName: 200,
  companyType: 60, regNumber: 80, industry: 80,
  domain: 100, website: 200, linkedin: 200,
  landline: 30, landlineExtn: 10, fax: 30,
  email: 180,
  addr_line: 200, addr_city: 80, addr_state: 80, addr_pin: 20, addr_country: 60,
  pc_first_name: 60, pc_last_name: 60,
  pc_designation: 100, pc_department: 100,
  pc_phone: 30, pc_mobile: 30,
};
const DOMAIN_REGEX = /^[a-z0-9][a-z0-9\-.]+\.[a-z]{2,}$/i;
const URL_REGEX = /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/.*)?$/i;
// Email regex — must match the backend validator and Excel parser (G8 fix)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// Client code must follow the format DZIND-C### (e.g. DZIND-C001). Mirrors
// CLIENT_CODE_REGEX in controllers/clientController.js + utils/excel.js.
const CLIENT_CODE_REGEX = /^DZIND-C\d+$/;

// Auto-generate a unique brand colour per client from the client code.
// Deterministic: the same code always produces the same colour.
// Uses HSL with fixed saturation/lightness so the result is always a vibrant
// mid-tone (no muddy greys or near-white).
function autoColorForCode(code) {
  const s = String(code || "").trim() || "DEFAULT";
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  // HSL → hex helper
  const sat = 0.62, light = 0.48;
  const k = n => (n + hue / 30) % 12;
  const a = sat * Math.min(light, 1 - light);
  const f = n => light - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = x => Math.round(255 * x).toString(16).padStart(2, "0");
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`.toUpperCase();
}

// "" / undefined / null → null. Anything else passes through.
const nul = v => (v === "" || v === null || v === undefined) ? null : v;

// Phone-field sanitiser:
//  • keeps only [0-9 + - space ( )]
//  • caps total char count at `maxChars` (matches varchar column width)
//  • caps DIGIT count at 15 (matches backend isValidPhone — 7-15 digits)
// Formatting characters between digits are kept untouched.
function clampPhone(raw, maxChars) {
  const cleaned = String(raw ?? "").replace(/[^0-9+\-\s()]/g, "").slice(0, maxChars);
  let digits = 0, out = "";
  for (const c of cleaned) {
    if (/\d/.test(c)) {
      if (digits >= 15) continue;   // drop the extra digit, keep typing formatting
      digits++;
    }
    out += c;
  }
  return out;
}

// Convert raw API client row (snake_case) → camelCase shape used everywhere on
// this page. One source of truth — keeps detail fetcher, post-import reload,
// and create-success refresh in lockstep.
function normalizeClient(d, prev = {}) {
  return {
    ...prev,
    ...d,
    id: d.id ?? prev.id,
    code: d.code ?? prev.code,
    name: d.name ?? prev.name ?? "",
    status: d.status ?? prev.status ?? "active",
    industry: d.industry ?? prev.industry ?? "",
    color_hex: d.color_hex ?? prev.color_hex ?? "#E8520A",
    domain: d.primary_domain ?? prev.domain ?? "",
    legalName: d.legal_name ?? "",
    companyType: d.company_type ?? "",
    regNumber: d.reg_number ?? "",
    website: d.website ?? "",
    linkedin: d.linkedin ?? "",
    founded: d.founded_year ? String(d.founded_year) : "",
    empRange: d.emp_range ?? "",
    revenueRange: d.revenue_range ?? "",
    notes: d.notes ?? "",
    primaryStakeholderId: d.primary_stakeholder_id ?? prev.primaryStakeholderId ?? null,
    stakeholders: (d.stakeholders || []).map(s => {
      const fn = s.first_name || s.firstName || "";
      const ln = s.last_name || s.lastName || "";
      return { ...s, deptId: s.dept_id ?? s.deptId ?? null, active: Boolean(s.active), firstName: fn, lastName: ln, name: [fn, ln].filter(Boolean).join(" ") || s.name || "" };
    }),
    departments: d.departments || [],
    domains: (d.domains || []).map(x => typeof x === "string" ? { id: x, domain: x } : x),
    communication: {
      landline: d.landline1 || "",
      landlineExtn: d.landline1_extn || "",
      landline2: d.landline2 || "",
      landline2Extn: d.landline2_extn || "",
      fax: d.fax1 || "",
      fax2: d.fax2 || "",
      receptionEmail: d.email_reception || "",
      financeEmail: d.email_finance || "",
      hrEmail: d.email_hr || "",
      supportEmail: d.email_support || "",
    },
    address: {
      line1: d.addr_line1 || "", line2: d.addr_line2 || "",
      city: d.addr_city || "", state: d.addr_state || "",
      pin: d.addr_pin || "", country: d.addr_country || "India",
    },
    billingAddress: {
      line1: d.billing_line1 || "",
      line2: d.billing_line2 || "",
      city: d.billing_city || "",
      state: d.billing_state || "",
      pin: d.billing_pin || "",
      country: d.billing_country || "India",
    },
    billingSame: Boolean(d.billing_same),
    primaryContact: {
      name: [d.pc_first_name, d.pc_last_name].filter(Boolean).join(" "),
      firstName: d.pc_first_name || "",
      lastName: d.pc_last_name || "",
      email: d.pc_email || "",
      phone: d.pc_phone || "",
      designation: d.pc_designation || "",
      department: d.pc_department || "",
      mobile: d.pc_mobile || "",
    },
    contacts: (d.contacts || []).map(ct => ({
      ...ct,
      firstName: ct.first_name || ct.firstName || "",
      lastName: ct.last_name || ct.lastName || "",
      name: ct.name || [ct.first_name || ct.firstName, ct.last_name || ct.lastName].filter(Boolean).join(" ") || "",
    })),
  };
}

const SHTable = ({ rows, onUpdate, onDelete, onConfirmDelete, onToggle, activeEmails = [], primaryStakeholderId, onSetPrimary }) => {
  if (!rows.length) return null;
  const showPrimary = !!onSetPrimary;
  return (
    <div style={{ border: "1.5px solid #F1F5F9", borderRadius: 10, overflow: "hidden" }}>
      <table>
        <thead>
          <tr>
            {[
              <>First Name <span style={{ color: "#EF4444" }}>*</span></>,
              <>Last Name <span style={{ color: "#EF4444" }}>*</span></>,
              <>Email <span style={{ color: "#EF4444" }}>*</span></>,
              "Designation", "Active", showPrimary ? "Primary" : "", "",
            ].map((h, i) => (
              <th key={i} style={{ padding: "8px 12px", fontSize: 10, color: "#94A3B8", fontWeight: 700, letterSpacing: 0.7, textTransform: "uppercase" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((s, i) => {
            const isPrimary = !!primaryStakeholderId && s.id === primaryStakeholderId;
            return (
              <tr key={s.id} style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFF", opacity: s.active ? 1 : 0.55 }}>
                <td style={{ padding: "9px 12px", minWidth: 130 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <input className="inp" value={s.firstName || ""} onChange={e => onUpdate(s.id, "firstName", e.target.value)} style={{ fontSize: 12, padding: "6px 9px", width: "100%" }} placeholder="First" />
                    {activeEmails.includes(s.email) && (
                      <span style={{ background: "#D1FAE5", color: "#065F46", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 100, whiteSpace: "nowrap", alignSelf: "flex-start" }}>Active Review</span>
                    )}
                  </div>
                </td>
                <td style={{ padding: "9px 12px", minWidth: 130 }}>
                  <input className="inp" value={s.lastName || ""} onChange={e => onUpdate(s.id, "lastName", e.target.value)} style={{ fontSize: 12, padding: "6px 9px", width: "100%" }} placeholder="Last" />
                </td>
                <td style={{ padding: "9px 12px" }}><input className="inp" value={s.email} onChange={e => onUpdate(s.id, "email", e.target.value)} style={{ fontSize: 12, padding: "6px 9px" }} /></td>
                <td style={{ padding: "9px 12px" }}><input className="inp" value={s.designation} onChange={e => onUpdate(s.id, "designation", e.target.value)} style={{ fontSize: 12, padding: "6px 9px" }} /></td>
                <td style={{ padding: "9px 12px" }}>
                  <button onClick={() => onToggle(s.id)} style={{ width: 40, height: 22, borderRadius: 100, border: "none", cursor: "pointer", background: s.active ? "#10B981" : "#E2E8F0", position: "relative" }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: s.active ? 21 : 3, transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }} />
                  </button>
                </td>
                {showPrimary && (
                  <td style={{ padding: "9px 12px" }}>
                    {isPrimary ? (
                      <span style={{
                        fontSize: 10, fontWeight: 700, background: "#FFF5F0", color: "#E8520A",
                        border: "1px solid #FDBA74", padding: "3px 10px", borderRadius: 100, whiteSpace: "nowrap"
                      }}>
                        &#9733; Primary
                      </span>
                    ) : (
                      <button onClick={() => onSetPrimary && onSetPrimary(s.id)} style={{
                        fontSize: 10, fontWeight: 600, background: "#F8FAFC", color: "#64748B",
                        border: "1.5px dashed #CBD5E1", padding: "3px 10px", borderRadius: 100,
                        cursor: "pointer", whiteSpace: "nowrap", transition: "all .15s"
                      }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = "#E8520A"; e.currentTarget.style.color = "#E8520A"; e.currentTarget.style.background = "#FFF5F0"; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = "#CBD5E1"; e.currentTarget.style.color = "#64748B"; e.currentTarget.style.background = "#F8FAFC"; }}>
                        Set Primary
                      </button>
                    )}
                  </td>
                )}
                <td style={{ padding: "9px 12px" }}>
                  <button className="btn-danger" onClick={() => onConfirmDelete(s.id, s.name)}>Remove</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AddClientModal = ({ onAdd, onClose, visible, existingClients = [] }) => {
  const [tab, setTab] = useState(0);
  const TABS = ["Company Info", "Communication", "Contacts", "Setup"];
  const TAB_COLORS = ["#E8520A", "#3B82F6", "#8B5CF6", "#10B981"];

  const [f, setF] = useState({
    code: "",  // backend auto-generates; we just preview the next number
    codeManual: false, // true once the user edits the suggested code
    status: "onboarding", name: "", legalName: "", industry: "Healthcare",
    companyType: "Private Limited", regNumber: "", domain: "", website: "",
    linkedin: "", founded: "", empRange: "", revenueRange: "",
    landline: "", landlineExtn: "", landline2: "", landline2Extn: "",
    fax: "", fax2: "",
    receptionEmail: "", financeEmail: "", hrEmail: "", supportEmail: "",
    addr1: "", addr2: "", city: "", state: "", pin: "", country: "India",
    billingSame: true,
    bAddr1: "", bAddr2: "", bCity: "", bState: "", bPin: "", bCountry: "India",
    pcFirstName: "", pcLastName: "", pcDesig: "", pcDept: "", pcEmail: "", pcPhone: "", pcMobile: "",
    firstDept: "", notes: "",
  });
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));

  // ── Typed input helpers ──────────────────────────────────────────────────────
  // Phone: digits, +, spaces, -, (, ) — for landlines, fax, mobile
  const phoneInp = (val, onChange, ph, maxLen = CLIENT_FIELD_MAX.landline) => (
    <input className="inp" value={val}
      onChange={e => onChange(clampPhone(e.target.value, maxLen))}
      onKeyDown={e => {
        if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
        if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
        if (!/^[0-9+\-\s()]$/.test(e.key)) e.preventDefault();
        // Block extra digit keystrokes once we're at 15 digits
        if (/[0-9]/.test(e.key) && (val || "").replace(/\D/g, "").length >= 15) e.preventDefault();
      }}
      placeholder={ph} inputMode="tel" maxLength={maxLen} />
  );

  // Extension: digits only (schema is varchar(10))
  const extnInp = (val, onChange, ph) => (
    <input className="inp" value={val}
      onChange={e => { const v = e.target.value.replace(/\D/g, "").slice(0, CLIENT_FIELD_MAX.landlineExtn); onChange(v); }}
      onKeyDown={e => {
        if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
        if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
        if (!/^[0-9]$/.test(e.key)) e.preventDefault();
      }}
      placeholder={ph} inputMode="numeric" maxLength={CLIENT_FIELD_MAX.landlineExtn} />
  );

  // PIN/ZIP: digits only
  const pinInp = (val, onChange, ph, maxLen = CLIENT_FIELD_MAX.addr_pin) => (
    <input className="inp" value={val}
      onChange={e => { const v = e.target.value.replace(/\D/g, "").slice(0, maxLen); onChange(v); }}
      onKeyDown={e => {
        if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
        if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
        if (!/^[0-9]$/.test(e.key)) e.preventDefault();
      }}
      placeholder={ph} inputMode="numeric" maxLength={maxLen} />
  );

  // Year: 4-digit number, no negatives
  const yearInp = (val, onChange, ph) => (
    <input className="inp" value={val}
      onChange={e => { const v = e.target.value.replace(/\D/g, "").slice(0, 4); onChange(v); }}
      onKeyDown={e => {
        if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
        if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
        if (!/^[0-9]$/.test(e.key)) e.preventDefault();
      }}
      placeholder={ph} inputMode="numeric" maxLength={4}
      style={{ borderColor: val && val.length === 4 && (parseInt(val) < 1800 || parseInt(val) > new Date().getFullYear()) ? "#EF4444" : "" }} />
  );

  // Email input with format hint on blur
  const emailInp = (val, onChange, ph, required = false, maxLen = CLIENT_FIELD_MAX.email) => {
    const emailOk = !val || EMAIL_REGEX.test(val);
    return (
      <input className="inp" type="email" value={val}
        onChange={e => onChange(e.target.value.slice(0, maxLen))}
        placeholder={ph} maxLength={maxLen}
        style={{ borderColor: (required && !val) || (val && !emailOk) ? "#EF4444" : "" }} />
    );
  };

  const [extraContacts, setExtraContacts] = useState([]);

  // Fetch the next auto-generated client code from the backend on first render
  // so the modal can preview it (read-only). The backend regenerates on save in
  // case another admin created a client in the meantime — preview is just UX.
  useEffect(() => {
    if (!visible) return;
    apiFetch("/api/clients/next-code")
      .then(r => r.json())
      .then(d => {
        if (d.success && d.code) {
          // Only fill the suggestion if the user hasn't typed their own code yet.
          setF(p => p.codeManual ? p : { ...p, code: d.code });
        }
      })
      .catch(() => { /* fall back to empty */ });
  }, [visible]);
  const addContact = () => setExtraContacts(p => [...p, { id: uid(), firstName: "", lastName: "", name: "", designation: "", department: "", email: "", phone: "", mobile: "", type: "Operations" }]);
  const updContact = (id, fld, v) => setExtraContacts(p => p.map(c => {
    if (c.id !== id) return c;
    const upd = { ...c, [fld]: v };
    if (fld === "firstName" || fld === "lastName") upd.name = [upd.firstName || "", upd.lastName || ""].filter(Boolean).join(" ");
    return upd;
  }));
  const remContact = id => setExtraContacts(p => p.filter(c => c.id !== id));

  const emailOk = v => !v || EMAIL_REGEX.test(v);
  const validationErrors = [];

  // Required — `code` is auto-generated server-side, but if the user typed
  // their own, validate format / length / local duplicate up front.
  if (f.codeManual) {
    const c = f.code.trim().toUpperCase();
    if (!c) validationErrors.push("Client code is required (or click \"Use auto-suggested\")");
    else if (!CLIENT_CODE_REGEX.test(c)) validationErrors.push("Client code must follow the format DZIND-C### (e.g. DZIND-C001) — the 'DZIND-C' prefix is required and the suffix must be digits only");
    else if (c.length > CLIENT_FIELD_MAX.code) validationErrors.push(`Client code must be ${CLIENT_FIELD_MAX.code} characters or less`);
    else if (existingClients.some(cl => String(cl.code || "").toUpperCase() === c)) {
      validationErrors.push(`Client code "${c}" is already used by another client`);
    }
  }
  if (!f.name.trim()) validationErrors.push("Client name is required");
  else if (f.name.length > CLIENT_FIELD_MAX.name) validationErrors.push(`Client name must be ${CLIENT_FIELD_MAX.name} characters or less`);
  if (!f.domain.trim()) validationErrors.push("Primary domain is required");
  else if (!DOMAIN_REGEX.test(f.domain.trim())) validationErrors.push("Primary domain does not look like a valid domain (e.g. acme.com)");
  else if (f.domain.length > CLIENT_FIELD_MAX.domain) validationErrors.push(`Primary domain must be ${CLIENT_FIELD_MAX.domain} characters or less`);
  if (!f.pcFirstName.trim()) validationErrors.push("Primary contact first name is required");
  if (!f.pcLastName.trim()) validationErrors.push("Primary contact last name is required");
  if (!f.pcEmail.trim()) validationErrors.push("Primary contact email is required");
  else if (!emailOk(f.pcEmail)) validationErrors.push("Primary contact email format is invalid");
  // #2 — at least one department required at creation
  if (!f.firstDept || !f.firstDept.trim()) validationErrors.push("At least one department is required");

  // Optional format/length
  if (f.legalName && f.legalName.length > CLIENT_FIELD_MAX.legalName) validationErrors.push(`Legal name must be ${CLIENT_FIELD_MAX.legalName} characters or less`);
  if (f.regNumber && f.regNumber.length > CLIENT_FIELD_MAX.regNumber) validationErrors.push(`Registration number must be ${CLIENT_FIELD_MAX.regNumber} characters or less`);
  if (f.website && f.website.length > CLIENT_FIELD_MAX.website) validationErrors.push(`Website must be ${CLIENT_FIELD_MAX.website} characters or less`);
  if (f.linkedin && f.linkedin.length > CLIENT_FIELD_MAX.linkedin) validationErrors.push(`LinkedIn URL must be ${CLIENT_FIELD_MAX.linkedin} characters or less`);
  if (f.receptionEmail && !emailOk(f.receptionEmail)) validationErrors.push("Reception email format is invalid");
  if (f.financeEmail && !emailOk(f.financeEmail)) validationErrors.push("Finance email format is invalid");
  if (f.hrEmail && !emailOk(f.hrEmail)) validationErrors.push("HR email format is invalid");
  if (f.supportEmail && !emailOk(f.supportEmail)) validationErrors.push("Support email format is invalid");
  if (f.founded && (parseInt(f.founded) < 1800 || parseInt(f.founded) > new Date().getFullYear())) validationErrors.push("Founded year is out of range");
  extraContacts.forEach((ec, i) => { if (ec.email && !emailOk(ec.email)) validationErrors.push(`Contact #${i + 1} email format is invalid`); });
  const valid = validationErrors.length === 0;

  const submit = () => {
    if (!valid) return;
    const newClient = {
      id: "CL" + uid(),
      code: f.code,
      codeManual: f.codeManual,
      name: f.name, legalName: f.legalName, industry: f.industry,
      companyType: f.companyType, regNumber: f.regNumber,
      domain: f.domain, domains: f.domain ? [{ id: "tmp_1", domain: f.domain }] : [], status: f.status,
      website: f.website, linkedin: f.linkedin,
      founded: f.founded, empRange: f.empRange, revenueRange: f.revenueRange,
      notes: f.notes,
      communication: {
        landline: f.landline, landlineExtn: f.landlineExtn,
        landline2: f.landline2, landline2Extn: f.landline2Extn,
        fax: f.fax, fax2: f.fax2,
        receptionEmail: f.receptionEmail, financeEmail: f.financeEmail,
        hrEmail: f.hrEmail, supportEmail: f.supportEmail,
      },
      address: { line1: f.addr1, line2: f.addr2, city: f.city, state: f.state, pin: f.pin, country: f.country },
      billingAddress: f.billingSame
        ? { line1: f.addr1, line2: f.addr2, city: f.city, state: f.state, pin: f.pin, country: f.country }
        : { line1: f.bAddr1, line2: f.bAddr2, city: f.bCity, state: f.bState, pin: f.bPin, country: f.bCountry },
      primaryContact: { firstName: f.pcFirstName, lastName: f.pcLastName, name: [f.pcFirstName, f.pcLastName].filter(Boolean).join(" "), designation: f.pcDesig, department: f.pcDept, email: f.pcEmail, phone: f.pcPhone, mobile: f.pcMobile },
      additionalContacts: extraContacts,
      departments: f.firstDept ? [{ id: "D" + uid(), name: f.firstDept }] : [],
      stakeholders: []
    };
    onAdd(newClient);
  };

  const secStyle = (color) => ({ background: "#FAFBFF", border: `1.5px solid ${color}22`, borderRadius: 10, padding: "18px 20px", marginBottom: 0 });
  const secHead = (color, label) => (
    <div style={{ fontSize: 11, color, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.9, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 3, height: 14, background: color, borderRadius: 2 }} />{label}
    </div>
  );
  const G2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
  const G3 = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 };

  if (!visible) return null;
  return (
    <div className="modal-overlay" style={{ left: 236 }}>
      <div className="modal" style={{ maxWidth: 680, width: "min(680px, calc(100% - 32px))" }}>

        {/* Header */}
        <div style={{ padding: "22px 28px 0", background: "linear-gradient(135deg,#0D1B2A,#1E3A5F)", borderRadius: "18px 18px 0 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
            <div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#fff" }}>Add New Client</div>
              <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 3 }}>Complete client profile — all sections optional except name, domain and primary contact</div>
            </div>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,.1)", border: "none", color: "#fff", width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>&#215;</button>
          </div>
          {/* Tab bar */}
          <div style={{ display: "flex", gap: 0 }}>
            {TABS.map((t, i) => (
              <button key={i} onClick={() => setTab(i)} style={{
                flex: 1, padding: "10px 0", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600,
                background: tab === i ? "#fff" : "transparent",
                color: tab === i ? TAB_COLORS[i] : "#94A3B8",
                borderRadius: tab === i ? "8px 8px 0 0" : 0,
                transition: "all .2s"
              }}>
                <span style={{ marginRight: 6 }}>{["🏢", "📞", "👥", "⚙️"][i]}</span>
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "22px 28px", display: "flex", flexDirection: "column", gap: 14, overflowY: "auto", maxHeight: "60vh" }}>

          {/* ── TAB 0: Company Info ── */}
          {tab === 0 && (
            <>
              <div style={secStyle("#E8520A")}>
                {secHead("#E8520A", "Client Identity")}
                <div style={G2}>
                  <FieldRow label="Client Code (auto-suggested)">
                    <input className="inp" value={f.code}
                      onChange={e => {
                        // Keep letters, digits, - and _ only; uppercase; max 10 chars.
                        const v = e.target.value.replace(/[^A-Za-z0-9_\-]/g, "").toUpperCase().slice(0, CLIENT_FIELD_MAX.code);
                        setF(p => ({ ...p, code: v, codeManual: true }));
                      }}
                      onKeyDown={e => {
                        if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
                        if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
                        if (!/^[A-Za-z0-9_\-]$/.test(e.key)) e.preventDefault();
                      }}
                      placeholder="Loading next client code…"
                      maxLength={CLIENT_FIELD_MAX.code}
                      title="Auto-suggested — edit to set manually (letters, digits, - and _ only; max 20 chars)"
                      style={{ fontFamily: "'JetBrains Mono',monospace", textTransform: "uppercase" }} />
                    {f.codeManual && (
                      <div style={{ marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10 }}>
                        <span style={{ color: "#64748B" }}>Manual entry — must be unique</span>
                        <button type="button"
                          onClick={() => {
                            setF(p => ({ ...p, codeManual: false, code: "" }));
                            apiFetch("/api/clients/next-code")
                              .then(r => r.json())
                              .then(d => { if (d.success && d.code) setF(p => p.codeManual ? p : { ...p, code: d.code }); })
                              .catch(() => { });
                          }}
                          style={{ background: "none", border: "none", color: "#3B82F6", cursor: "pointer", padding: 0, fontSize: 10, textDecoration: "underline" }}>
                          ↻ Use auto-suggested
                        </button>
                      </div>
                    )}
                    <div style={{
                      marginTop: 6, padding: "6px 10px", background: "#FFFBEB",
                      border: "1px solid #FDE68A", borderRadius: 6, fontSize: 10, color: "#92400E",
                      display: "flex", alignItems: "center", gap: 6
                    }}>
                      <span>⚠</span>
                      <span>Client Code is permanent — it cannot be updated after the client is created.</span>
                    </div>
                  </FieldRow>
                  <FieldRow label="Onboarding Status">
                    <select className="inp" value={f.status} onChange={e => upd("status", e.target.value)}>
                      <option value="onboarding">Onboarding</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </FieldRow>
                </div>
                <div style={{ marginTop: 12 }}>
                  <FieldRow label="Trading / Brand Name *"><input className="inp" value={f.name} onChange={e => upd("name", e.target.value.slice(0, CLIENT_FIELD_MAX.name))} maxLength={CLIENT_FIELD_MAX.name} placeholder="e.g. Infosys BPM" /></FieldRow>
                </div>
                <div style={{ marginTop: 12 }}>
                  <FieldRow label="Registered Legal Name"><input className="inp" value={f.legalName} onChange={e => upd("legalName", e.target.value.slice(0, CLIENT_FIELD_MAX.legalName))} maxLength={CLIENT_FIELD_MAX.legalName} placeholder="e.g. Infosys BPM Limited" /></FieldRow>
                </div>
                <div style={{ ...G3, marginTop: 12 }}>
                  <FieldRow label="Industry">
                    <select className="inp" value={f.industry} onChange={e => upd("industry", e.target.value)}>
                      {["Healthcare", "Telecom", "Banking", "Automobile", "IT Services", "Finance", "Insurance", "Retail", "Manufacturing", "Pharma", "Education", "Other"].map(i => <option key={i}>{i}</option>)}
                    </select>
                  </FieldRow>
                  <FieldRow label="Company Type">
                    <select className="inp" value={f.companyType} onChange={e => upd("companyType", e.target.value)}>
                      {["Private Limited", "Public Limited", "LLP", "Partnership", "Sole Proprietorship", "Government", "NGO", "Other"].map(i => <option key={i}>{i}</option>)}
                    </select>
                  </FieldRow>
                  <FieldRow label="Registration No."><input className="inp" value={f.regNumber} onChange={e => upd("regNumber", e.target.value.slice(0, CLIENT_FIELD_MAX.regNumber))} maxLength={CLIENT_FIELD_MAX.regNumber} placeholder="e.g. U72200MH2006" /></FieldRow>
                </div>
                <div style={{ ...G2, marginTop: 12 }}>
                  <FieldRow label="Primary Domain *">
                    <input className="inp" value={f.domain}
                      onChange={e => upd("domain", e.target.value.slice(0, CLIENT_FIELD_MAX.domain))}
                      maxLength={CLIENT_FIELD_MAX.domain}
                      placeholder="e.g. infosys.com"
                      style={{ borderColor: f.domain && !DOMAIN_REGEX.test(f.domain.trim()) ? "#EF4444" : "" }} />
                  </FieldRow>
                  <FieldRow label="Website"><input className="inp" value={f.website} onChange={e => upd("website", e.target.value.slice(0, CLIENT_FIELD_MAX.website))} maxLength={CLIENT_FIELD_MAX.website} placeholder="https://www.infosys.com" /></FieldRow>
                </div>
                <div style={{ ...G3, marginTop: 12 }}>
                  <FieldRow label="LinkedIn URL"><input className="inp" value={f.linkedin} onChange={e => upd("linkedin", e.target.value.slice(0, CLIENT_FIELD_MAX.linkedin))} maxLength={CLIENT_FIELD_MAX.linkedin} placeholder="linkedin.com/company/..." /></FieldRow>
                  <FieldRow label="Founded Year">{yearInp(f.founded, v => upd("founded", v), "e.g. 1998")}</FieldRow>
                  <FieldRow label="Employee Strength">
                    <select className="inp" value={f.empRange} onChange={e => upd("empRange", e.target.value)}>
                      <option value="">Select range</option>
                      {["1-10", "11-50", "51-200", "201-500", "501-1000", "1000-5000", "5000+"].map(r => <option key={r}>{r}</option>)}
                    </select>
                  </FieldRow>
                </div>
                <div style={{ marginTop: 12 }}>
                  <FieldRow label="Annual Revenue Range (USD)">
                    <select className="inp" value={f.revenueRange} onChange={e => upd("revenueRange", e.target.value)}>
                      <option value="">Select range</option>
                      {["Under $1M", "$1M–$10M", "$10M–$50M", "$50M–$200M", "$200M–$1B", "Above $1B"].map(r => <option key={r}>{r}</option>)}
                    </select>
                  </FieldRow>
                </div>
              </div>
            </>
          )}

          {/* ── TAB 1: Communication ── */}
          {tab === 1 && (
            <>
              <div style={secStyle("#3B82F6")}>
                {secHead("#3B82F6", "Telephone & Fax")}
                <div style={G3}>
                  <FieldRow label="Landline 1">{phoneInp(f.landline, v => upd("landline", v), "+91 44 2222 0000")}</FieldRow>
                  <FieldRow label="Extn. (Landline 1)">{extnInp(f.landlineExtn, v => upd("landlineExtn", v), "101")}</FieldRow>
                  <FieldRow label="Fax 1">{phoneInp(f.fax, v => upd("fax", v), "+91 44 2222 0001")}</FieldRow>
                </div>
                <div style={{ ...G3, marginTop: 12 }}>
                  <FieldRow label="Landline 2">{phoneInp(f.landline2, v => upd("landline2", v), "+91 44 3333 0000")}</FieldRow>
                  <FieldRow label="Extn. (Landline 2)">{extnInp(f.landline2Extn, v => upd("landline2Extn", v), "202")}</FieldRow>
                  <FieldRow label="Fax 2">{phoneInp(f.fax2, v => upd("fax2", v), "+91 44 3333 0001")}</FieldRow>
                </div>
              </div>

              <div style={secStyle("#6366F1")}>
                {secHead("#6366F1", "Common / Shared Email IDs")}
                <div style={G2}>
                  <FieldRow label="Reception / General">{emailInp(f.receptionEmail, v => upd("receptionEmail", v), "info@client.com")}</FieldRow>
                  <FieldRow label="Finance / Accounts">{emailInp(f.financeEmail, v => upd("financeEmail", v), "accounts@client.com")}</FieldRow>
                </div>
                <div style={{ ...G2, marginTop: 12 }}>
                  <FieldRow label="HR / People">{emailInp(f.hrEmail, v => upd("hrEmail", v), "hr@client.com")}</FieldRow>
                  <FieldRow label="Support / Helpdesk">{emailInp(f.supportEmail, v => upd("supportEmail", v), "support@client.com")}</FieldRow>
                </div>
              </div>

              <div style={secStyle("#F59E0B")}>
                {secHead("#F59E0B", "Registered Office Address")}
                <div style={{ marginBottom: 12 }}>
                  <FieldRow label="Address Line 1"><input className="inp" value={f.addr1} onChange={e => upd("addr1", e.target.value.slice(0, CLIENT_FIELD_MAX.addr_line))} maxLength={CLIENT_FIELD_MAX.addr_line} placeholder="Building name, floor, street" /></FieldRow>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <FieldRow label="Address Line 2"><input className="inp" value={f.addr2} onChange={e => upd("addr2", e.target.value.slice(0, CLIENT_FIELD_MAX.addr_line))} maxLength={CLIENT_FIELD_MAX.addr_line} placeholder="Area, landmark" /></FieldRow>
                </div>
                <div style={G3}>
                  <FieldRow label="City"><input className="inp" value={f.city} onChange={e => upd("city", e.target.value.slice(0, CLIENT_FIELD_MAX.addr_city))} maxLength={CLIENT_FIELD_MAX.addr_city} placeholder="e.g. Chennai" /></FieldRow>
                  <FieldRow label="State"><input className="inp" value={f.state} onChange={e => upd("state", e.target.value.slice(0, CLIENT_FIELD_MAX.addr_state))} maxLength={CLIENT_FIELD_MAX.addr_state} placeholder="e.g. Tamil Nadu" /></FieldRow>
                  <FieldRow label="PIN / ZIP">{pinInp(f.pin, v => upd("pin", v), "600001", CLIENT_FIELD_MAX.addr_pin)}</FieldRow>
                </div>
                <div style={{ marginTop: 12 }}>
                  <FieldRow label="Country">
                    <select className="inp" value={f.country} onChange={e => upd("country", e.target.value)}>
                      {["India", "USA", "UK", "Australia", "Singapore", "UAE", "Canada", "Germany", "Other"].map(c => <option key={c}>{c}</option>)}
                    </select>
                  </FieldRow>
                </div>
              </div>

              <div style={secStyle("#10B981")}>
                {secHead("#10B981", "Billing Address")}
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#475569", fontWeight: 500, cursor: "pointer", marginBottom: 14 }}>
                  <input type="checkbox" checked={f.billingSame} onChange={e => upd("billingSame", e.target.checked)} style={{ width: "auto", accentColor: "#E8520A" }} />
                  Same as Registered Office Address
                </label>
                {!f.billingSame && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <FieldRow label="Address Line 1"><input className="inp" value={f.bAddr1} onChange={e => upd("bAddr1", e.target.value.slice(0, CLIENT_FIELD_MAX.addr_line))} maxLength={CLIENT_FIELD_MAX.addr_line} placeholder="Building name, floor, street" /></FieldRow>
                    <FieldRow label="Address Line 2"><input className="inp" value={f.bAddr2} onChange={e => upd("bAddr2", e.target.value.slice(0, CLIENT_FIELD_MAX.addr_line))} maxLength={CLIENT_FIELD_MAX.addr_line} placeholder="Area, landmark" /></FieldRow>
                    <div style={G3}>
                      <FieldRow label="City"><input className="inp" value={f.bCity} onChange={e => upd("bCity", e.target.value.slice(0, CLIENT_FIELD_MAX.addr_city))} maxLength={CLIENT_FIELD_MAX.addr_city} placeholder="City" /></FieldRow>
                      <FieldRow label="State"><input className="inp" value={f.bState} onChange={e => upd("bState", e.target.value.slice(0, CLIENT_FIELD_MAX.addr_state))} maxLength={CLIENT_FIELD_MAX.addr_state} placeholder="State" /></FieldRow>
                      <FieldRow label="PIN / ZIP">{pinInp(f.bPin, v => upd("bPin", v), "PIN", CLIENT_FIELD_MAX.addr_pin)}</FieldRow>
                    </div>
                    <FieldRow label="Country">
                      <select className="inp" value={f.bCountry} onChange={e => upd("bCountry", e.target.value)}>
                        {["India", "USA", "UK", "Australia", "Singapore", "UAE", "Canada", "Germany", "Other"].map(c => <option key={c}>{c}</option>)}
                      </select>
                    </FieldRow>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── TAB 2: Contacts ── */}
          {tab === 2 && (
            <>
              <div style={secStyle("#3B82F6")}>
                {secHead("#3B82F6", <>Primary Contact <span style={{ color: "#EF4444" }}>*</span></>)}
                <div style={G2}>
                  <FieldRow label="First Name *"><input className="inp" value={f.pcFirstName} onChange={e => upd("pcFirstName", e.target.value.slice(0, CLIENT_FIELD_MAX.pc_first_name))} maxLength={CLIENT_FIELD_MAX.pc_first_name} placeholder="e.g. Sanjay" /></FieldRow>
                  <FieldRow label="Last Name *"><input className="inp" value={f.pcLastName} onChange={e => upd("pcLastName", e.target.value.slice(0, CLIENT_FIELD_MAX.pc_last_name))} maxLength={CLIENT_FIELD_MAX.pc_last_name} placeholder="e.g. Mehta" /></FieldRow>
                  <FieldRow label="Designation"><input className="inp" value={f.pcDesig} onChange={e => upd("pcDesig", e.target.value.slice(0, CLIENT_FIELD_MAX.pc_designation))} maxLength={CLIENT_FIELD_MAX.pc_designation} placeholder="e.g. VP Operations" /></FieldRow>
                </div>
                <div style={{ marginTop: 12 }}>
                  <FieldRow label="Department"><input className="inp" value={f.pcDept} onChange={e => upd("pcDept", e.target.value.slice(0, CLIENT_FIELD_MAX.pc_department))} maxLength={CLIENT_FIELD_MAX.pc_department} placeholder="e.g. Operations" /></FieldRow>
                </div>
                <div style={{ ...G3, marginTop: 12 }}>
                  <FieldRow label="Work Email *">{emailInp(f.pcEmail, v => upd("pcEmail", v), "sanjay@client.com", true)}</FieldRow>
                  <FieldRow label="Direct Phone">{phoneInp(f.pcPhone, v => upd("pcPhone", v), "+91 44 2222 0101")}</FieldRow>
                  <FieldRow label="Mobile">{phoneInp(f.pcMobile, v => upd("pcMobile", v), "+91 98765 00000")}</FieldRow>
                </div>
              </div>

              <div style={secStyle("#8B5CF6")}>
                {secHead("#8B5CF6", "Additional Contacts")}
                {extraContacts.length === 0 && (
                  <div style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic", marginBottom: 12 }}>No additional contacts yet. Add Finance, Legal, Operations, Technical or other POCs.</div>
                )}
                {extraContacts.map((c, idx) => (
                  <div key={c.id} style={{ background: "#fff", border: "1.5px solid #EDE9FE", borderLeft: "4px solid #8B5CF6", borderRadius: 10, padding: "14px 16px", marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#6D28D9" }}>Contact #{idx + 1}</div>
                      <button className="btn-danger" onClick={() => remContact(c.id)} style={{ padding: "3px 8px", fontSize: 11 }}>Remove</button>
                    </div>
                    <div style={G2}>
                      <FieldRow label="First Name"><input className="inp" value={c.firstName || ""} onChange={e => updContact(c.id, "firstName", e.target.value.slice(0, CLIENT_FIELD_MAX.pc_first_name))} maxLength={CLIENT_FIELD_MAX.pc_first_name} placeholder="First name" /></FieldRow>
                      <FieldRow label="Last Name"><input className="inp" value={c.lastName || ""} onChange={e => updContact(c.id, "lastName", e.target.value.slice(0, CLIENT_FIELD_MAX.pc_last_name))} maxLength={CLIENT_FIELD_MAX.pc_last_name} placeholder="Last name" /></FieldRow>
                      <FieldRow label="Type / Function">
                        <select className="inp" value={c.type} onChange={e => updContact(c.id, "type", e.target.value)}>
                          {["Operations", "Finance / Accounts", "Legal / Compliance", "HR / Talent", "IT / Technical", "Procurement", "Executive", "Other"].map(t => <option key={t}>{t}</option>)}
                        </select>
                      </FieldRow>
                    </div>
                    <div style={{ ...G2, marginTop: 10 }}>
                      <FieldRow label="Designation"><input className="inp" value={c.designation} onChange={e => updContact(c.id, "designation", e.target.value.slice(0, CLIENT_FIELD_MAX.pc_designation))} maxLength={CLIENT_FIELD_MAX.pc_designation} placeholder="e.g. Finance Manager" /></FieldRow>
                      <FieldRow label="Department"><input className="inp" value={c.department} onChange={e => updContact(c.id, "department", e.target.value.slice(0, CLIENT_FIELD_MAX.pc_department))} maxLength={CLIENT_FIELD_MAX.pc_department} placeholder="e.g. Finance" /></FieldRow>
                    </div>
                    <div style={{ ...G3, marginTop: 10 }}>
                      <FieldRow label="Email">{emailInp(c.email, v => updContact(c.id, "email", v), "email@client.com")}</FieldRow>
                      <FieldRow label="Direct Phone">{phoneInp(c.phone, v => updContact(c.id, "phone", v), "+91 44 xxxx xxxx")}</FieldRow>
                      <FieldRow label="Mobile">{phoneInp(c.mobile, v => updContact(c.id, "mobile", v), "+91 9xxxx xxxxx")}</FieldRow>
                    </div>
                  </div>
                ))}
                <button className="btn-secondary" style={{ fontSize: 12, width: "100%", justifyContent: "center" }} onClick={addContact}>+ Add Another Contact</button>
              </div>
            </>
          )}

          {/* ── TAB 3: Setup ── */}
          {tab === 3 && (
            <>
              <div style={secStyle("#10B981")}>
                {secHead("#10B981", <>Initial Department <span style={{ color: "#EF4444" }}>*</span></>)}
                <FieldRow label="First Department Name *">
                  <input className="inp" value={f.firstDept}
                    onChange={e => upd("firstDept", e.target.value.slice(0, 120))}
                    maxLength={120}
                    placeholder="e.g. Medical Coding"
                    style={{ borderColor: !f.firstDept.trim() ? "#EF4444" : "" }} />
                </FieldRow>
                <div style={{ marginTop: 10, fontSize: 11, color: "#94A3B8", fontStyle: "italic" }}>At least one department is required. More departments, stakeholders, and domains can be added after creation.</div>
              </div>

              <div style={secStyle("#F59E0B")}>
                {secHead("#F59E0B", "Internal Notes")}
                <FieldRow label="Notes (internal only)">
                  <textarea className="inp" rows={3} value={f.notes}
                    onChange={e => upd("notes", e.target.value.slice(0, 200))}
                    maxLength={200}
                    placeholder="Anything worth recording — onboarding context, key accounts, SLAs, special arrangements…"
                    style={{ resize: "vertical", fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6 }} />
                </FieldRow>
                <div style={{ marginTop: 6, fontSize: 11, color: "#94A3B8", fontStyle: "italic" }}>Not shown to the client. Max 200 characters.</div>
              </div>

              <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "16px 18px" }}>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 13, color: "#166534", marginBottom: 10 }}>Summary Preview</div>
                {[
                  ["Client Name", f.name || "—"],
                  ["Legal Name", f.legalName || "—"],
                  ["Industry", f.industry],
                  ["Company Type", f.companyType],
                  ["Domain", f.domain || "—"],
                  ["Website", f.website || "—"],
                  ["Primary Contact", (f.pcFirstName || f.pcLastName) ? `${[f.pcFirstName, f.pcLastName].filter(Boolean).join(" ")} (${f.pcEmail || "no email"})` : "—"],
                  ["Phone / Mobile", [f.pcPhone, f.pcMobile].filter(Boolean).join(" · ") || "—"],
                  ["Landline", f.landline ? `${f.landline}${f.landlineExtn ? " extn " + f.landlineExtn : ""}` : "—"],
                  ["City", f.city || "—"],
                  ["Additional Contacts", extraContacts.length > 0 ? extraContacts.length + " added" : "None"],
                  ["Status", f.status],
                ].map(([l, v]) => (
                  <div key={l} style={{ display: "flex", gap: 10, fontSize: 12, marginBottom: 5, borderBottom: "1px solid #DCFCE7", paddingBottom: 5 }}>
                    <span style={{ color: "#64748B", width: 150, flexShrink: 0 }}>{l}</span>
                    <span style={{ fontWeight: 600, color: "#1E293B" }}>{v}</span>
                  </div>
                ))}
              </div>

              {!valid && (
                <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", fontSize: 12, color: "#991B1B" }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Please fix the following before creating:</div>
                  {validationErrors.map((e, i) => <div key={i} style={{ marginBottom: 3 }}>• {e}</div>)}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 28px", borderTop: "1px solid #F1F5F9", display: "flex", gap: 10, alignItems: "center", background: "#FAFBFF", borderRadius: "0 0 18px 18px" }}>
          {tab > 0 && <button className="btn-secondary" style={{ fontSize: 13 }} onClick={() => setTab(t => t - 1)}>&#8592; Back</button>}
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: 5 }}>
              {TABS.map((_, i) => (
                <div key={i} style={{ height: 4, flex: 1, borderRadius: 100, background: i <= tab ? TAB_COLORS[tab] : "#E2E8F0", transition: "background .3s" }} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>Step {tab + 1} of {TABS.length} — {TABS[tab]}</div>
          </div>
          {tab < TABS.length - 1
            ? <button className="btn-primary" style={{ fontSize: 13 }} onClick={() => setTab(t => t + 1)}>Next &#8594;</button>
            : <button className="btn-primary" style={{ fontSize: 14, padding: "10px 24px" }} disabled={!valid} onClick={submit}>Create Client</button>
          }
        </div>
      </div>
    </div>
  );
};

// ── Client Excel Import Modal ─────────────────────────────────────────────────
const ClientImportModal = ({ onClose, importFile, setImportFile, onImport, loading, result, onDownloadTemplate, templateLoading }) => {
  const hasResult = !!result;
  const allErrors = result ? [...(result.parseErrors || []), ...(result.insertErrors || [])] : [];

  return (
    <div className="modal-overlay" style={{ left: 236 }}>
      <div className="modal" style={{ maxWidth: 600, width: "min(600px, calc(100% - 32px))" }}>

        {/* Header */}
        <div style={{ padding: "22px 28px 18px", background: "linear-gradient(135deg,#0D1B2A,#1E3A5F)", borderRadius: "18px 18px 0 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#fff" }}>Import Clients via Excel</div>
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 3 }}>Upload a filled template to bulk-insert or update clients</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.1)", border: "none", color: "#fff", width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>&#215;</button>
        </div>

        {/* Body */}
        <div style={{ padding: "22px 28px", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Step 1 — Download template */}
          <div style={{ background: "#F0F9FF", border: "1.5px solid #BAE6FD", borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#0369A1", marginBottom: 8 }}>Step 1 — Download Template</div>
            <div style={{ fontSize: 12, color: "#374151", marginBottom: 10 }}>
              Download the template with all column definitions, dropdowns and example data.
              Orange headers are required. Leave billing address blank if same as office.
            </div>
            <button
              className="btn-secondary"
              style={{ fontSize: 12, opacity: templateLoading ? 0.6 : 1, cursor: templateLoading ? "not-allowed" : "pointer" }}
              onClick={onDownloadTemplate}
              disabled={templateLoading}>
              {templateLoading ? "⏳ Preparing template…" : "⬇ Download Client_Import_Template.xlsx"}
            </button>
          </div>

          {/* Step 2 — Upload */}
          <div style={{ background: "#FAFBFF", border: "1.5px solid #E2E8F0", borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 8 }}>Step 2 — Upload Filled File</div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <label style={{
                flex: 1, border: "2px dashed #CBD5E1", borderRadius: 8, padding: "12px 16px",
                cursor: "pointer", textAlign: "center", fontSize: 12, color: "#64748B",
                background: importFile ? "#F0FDF4" : "#fff",
                borderColor: importFile ? "#86EFAC" : "#CBD5E1",
              }}>
                <input type="file" accept=".xlsx,.xls" style={{ display: "none" }}
                  onChange={e => { setImportFile(e.target.files[0] || null); }}
                />
                {importFile
                  ? <span style={{ color: "#166534", fontWeight: 600 }}>📄 {importFile.name}</span>
                  : <span>Click to select <strong>.xlsx</strong> file</span>
                }
              </label>
              {importFile && (
                <button className="btn-ghost" style={{ fontSize: 12, color: "#EF4444" }} onClick={() => setImportFile(null)}>✕ Clear</button>
              )}
            </div>
          </div>

          {/* Result — same visual treatment as the employee import-result modal */}
          {hasResult && (
            <>
              {result.success && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
                  {[
                    { label: "Total Rows", value: result.total, color: "#0D1B2A", bg: "#F8FAFC", border: "#E2E8F0" },
                    { label: "✓ Inserted", value: result.inserted, color: "#10B981", bg: "#F0FDF4", border: "#BBF7D0" },
                    { label: "↻ Updated", value: result.updated || 0, color: "#3B82F6", bg: "#EFF6FF", border: "#BFDBFE" },
                    { label: "✗ Errors", value: allErrors.length, color: "#EF4444", bg: "#FEF2F2", border: "#FECACA" },
                  ].map(s => (
                    <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: s.color, fontFamily: "'Sora',sans-serif" }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: s.color, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
              {!result.success && (
                <div style={{ background: "#FEF2F2", border: "1.5px solid #FECACA", borderRadius: 10, padding: "14px 18px", fontSize: 13, color: "#991B1B", fontWeight: 600 }}>
                  ✗ {result.message || "Import failed"}
                </div>
              )}
              {allErrors.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>
                    Error Details
                  </div>
                  <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #FEE2E2", borderRadius: 10, overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "100px 120px 1fr", gap: 0, background: "#FEF2F2", padding: "8px 14px", borderBottom: "1px solid #FEE2E2" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#991B1B", textTransform: "uppercase", letterSpacing: 0.4 }}>Row / Code</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#991B1B", textTransform: "uppercase", letterSpacing: 0.4 }}>Name</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#991B1B", textTransform: "uppercase", letterSpacing: 0.4 }}>Error(s)</div>
                    </div>
                    {allErrors.map((e, i) => (
                      <div key={i} style={{ display: "grid", gridTemplateColumns: "100px 120px 1fr", gap: 0, padding: "9px 14px", borderBottom: "1px solid #FFF5F5", background: i % 2 === 0 ? "#fff" : "#FFFAFA", alignItems: "start" }}>
                        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, fontWeight: 600, color: "#EF4444" }}>
                          {e.row ? `Row ${e.row}` : "DB Error"}<br />
                          <span style={{ fontWeight: 400, color: "#94A3B8", fontSize: 10 }}>{e.client_code || e.code || "—"}</span>
                        </div>
                        <div style={{ fontSize: 12, color: "#475569" }}>{e.name || "—"}</div>
                        <div>
                          {(Array.isArray(e.errors) ? e.errors : [e.error || e.message]).map((msg, j) => (
                            <div key={j} style={{ fontSize: 12, color: "#64748B", marginBottom: j < (e.errors?.length || 1) - 1 ? 4 : 0 }}>
                              <span style={{ color: "#EF4444", fontWeight: 700, marginRight: 4 }}>•</span>{msg}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 8 }}>
                    Fix these rows in your Excel file and re-import. Successfully inserted/updated rows are already saved.
                  </div>
                </div>
              )}
              {result.success && allErrors.length === 0 && (
                <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "14px 16px", fontSize: 13, color: "#065F46", textAlign: "center" }}>
                  🎉 All {result.total} row{result.total !== 1 ? "s" : ""} imported successfully with no errors.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer — after a successful import, swap the Import button for a single
            Done button that just closes the modal. */}
        <div style={{ padding: "14px 28px", borderTop: "1px solid #F1F5F9", display: "flex", gap: 10, background: "#FAFBFF", borderRadius: "0 0 18px 18px" }}>
          {hasResult && result.success ? (
            <>
              <div style={{ flex: 1 }} />
              <button className="btn-primary" style={{ fontSize: 13 }} onClick={onClose}>Done</button>
            </>
          ) : (
            <>
              <button className="btn-secondary" style={{ fontSize: 13 }} onClick={onClose}>Close</button>
              <div style={{ flex: 1 }} />
              <button
                className="btn-primary"
                style={{ fontSize: 13, opacity: !importFile || loading ? 0.6 : 1, cursor: !importFile || loading ? "not-allowed" : "pointer" }}
                disabled={!importFile || loading}
                onClick={onImport}
              >
                {loading ? "Importing…" : "⬆ Import"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ClientConfig = ({ clients, setClients, employees, allReviews, topBarProps }) => {
  const [selId, setSelId] = useState(() => {
    const saved = localStorage.getItem("epr_selected_client");
    if (saved && clients.find(c => c.id === saved)) return saved;
    return clients[0]?.id || "";
  });
  const setSelIdPersist = (id) => { setSelId(id); localStorage.setItem("epr_selected_client", id); };
  const [editMode, setEditMode] = useState(false);
  const [pcEditMode, setPcEditMode] = useState(false);
  const [commEditMode, setCommEditMode] = useState(false);
  const [addrEditMode, setAddrEditMode] = useState(false);
  const [toast, setToast] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [newDept, setNewDept] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [confirmDelSH, setConfirmDelSH] = useState(null); // { id, name }
  const [confirmDelDept, setConfirmDelDept] = useState(null); // { id, name }
  const [confirmDelDomain, setConfirmDelDomain] = useState(null); // { id, domain }
  const [newSHIds, setNewSHIds] = useState(new Set());
  const [notesEditMode, setNotesEditMode] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null); // { id, value }
  const [editingDept, setEditingDept] = useState(null);     // { id, value }
  const [toastType, setToastType] = useState("");
  const showToast = (msg, type = "") => { setToast(msg); setToastType(type); setTimeout(() => { setToast(""); setToastType(""); }, 2400); };

  // Reset edit modes when switching client
  useEffect(() => { setEditMode(false); setPcEditMode(false); setCommEditMode(false); setAddrEditMode(false); setContactsEditMode(false); setNotesEditMode(false); }, [selId]); // eslint-disable-line

  // Fetch full client details (stakeholders, departments, domains) when selId changes
  useEffect(() => {
    if (!selId) return;
    apiFetch(`/api/clients/${selId}`)
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data) {
          setClients(cs => cs.map(c => c.id === selId ? normalizeClient(d.data, c) : c));
        }
      })
      .catch(() => { });
    setNewSHIds(new Set()); // clear pending new stakeholders on client switch
  }, [selId]); // eslint-disable-line

  // Sync selId when clients list first loads
  useEffect(() => {
    if (clients.length > 0 && !clients.find(c => c.id === selId)) {
      setSelIdPersist(clients[0].id);
    }
  }, [clients]); // eslint-disable-line

  const client = clients.find(c => c.id === selId) || clients[0] || null;
  const activeDeptIds = new Set((client?.departments || []).map(d => d.id));
  // Orphaned dept stakeholders (dept deleted) fall back to client-level display
  const clientSH = (client?.stakeholders || []).filter(s => s.level === "client" || (s.level === "dept" && !activeDeptIds.has(s.deptId)));
  const deptSH = did => (client?.stakeholders || []).filter(s => s.level === "dept" && s.deptId === did);
  const empCount = cid => employees.filter(e => (e.allocations || []).some(a => a.clientId === cid)).length;

  // ── Sidebar filters ─────────────────────────────────────────────────────────
  // statusFilter:  Set of "active" | "onboarding" | "inactive" (empty = all)
  // allocFilter:   "" | "with" | "without"
  // shFilter:      "" | "with" | "without"
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [allocFilter, setAllocFilter] = useState("");
  const [shFilter, setShFilter] = useState("");
  const toggleStatusFilter = (s) => setStatusFilter(prev => {
    const n = new Set(prev);
    if (n.has(s)) n.delete(s); else n.add(s);
    return n;
  });
  const clearAllFilters = () => {
    setStatusFilter(new Set());
    setAllocFilter("");
    setShFilter("");
  };
  const filtersActive = statusFilter.size > 0 || !!allocFilter || !!shFilter;

  // Apply filters, then sort by client code ASC (DZIND-C001, DZIND-C002, ...).
  const filteredClients = clients
    .filter(c => {
      if (statusFilter.size > 0 && !statusFilter.has(c.status)) return false;
      if (allocFilter) {
        const has = empCount(c.id) > 0;
        if (allocFilter === "with" && !has) return false;
        if (allocFilter === "without" && has) return false;
      }
      if (shFilter) {
        const hasSH = (c.stakeholders || []).some(s => s.active);
        if (shFilter === "with" && !hasSH) return false;
        if (shFilter === "without" && hasSH) return false;
      }
      return true;
    })
    .slice()
    .sort((a, b) => String(a.code || "").localeCompare(String(b.code || ""), undefined, { numeric: true, sensitivity: "base" }));
  const activeEmails = (allReviews || [])
    .filter(r => (r.client || "").includes((client?.name || "").split(" ")[0]) && ["Initiated", "In Progress", "Submitted"].includes(r.status))
    .map(r => r.stakeholderEmail || r.stakeholder_email || "");

  // ── Stakeholders ─────────────────────────────────────────────────────────────
  const addSH = (level, deptId = null) => {
    const tempId = "new_" + uid();
    setNewSHIds(s => new Set([...s, tempId]));
    setClients(cs => cs.map(c => c.id === selId
      ? { ...c, stakeholders: [...(c.stakeholders || []), { id: tempId, firstName: "", lastName: "", name: "", email: "", designation: "", level, deptId, active: true }] }
      : c));
  };
  const updateSH = (sid, fld, v) => setClients(cs => cs.map(c => {
    if (c.id !== selId) return c;
    return {
      ...c, stakeholders: (c.stakeholders || []).map(s => {
        if (s.id !== sid) return s;
        const updated = { ...s, [fld]: v };
        if (fld === "firstName" || fld === "lastName") {
          updated.name = [updated.firstName || "", updated.lastName || ""].filter(Boolean).join(" ");
        }
        return updated;
      })
    };
  }));
  const deleteSH = async (sid) => {
    setClients(cs => cs.map(c => c.id === selId
      ? { ...c, stakeholders: (c.stakeholders || []).filter(s => s.id !== sid) }
      : c));
    setNewSHIds(prev => { const n = new Set(prev); n.delete(sid); return n; });
    if (!newSHIds.has(sid)) {
      try { await apiFetch(`/api/clients/${selId}/stakeholders/${sid}`, { method: "DELETE" }); } catch (e) { }
    }
  };
  const toggleSH = sid => setClients(cs => cs.map(c => c.id === selId
    ? { ...c, stakeholders: (c.stakeholders || []).map(s => s.id === sid ? { ...s, active: !s.active } : s) }
    : c));
  const setPrimary = async (sid) => {
    setClients(cs => cs.map(c => c.id === selId ? { ...c, primaryStakeholderId: sid } : c));
    try { await apiFetch(`/api/clients/${selId}/primary-stakeholder`, { method: "PUT", body: JSON.stringify({ stakeholder_id: sid }) }); } catch (e) { }
  };
  const saveStakeholders = async () => {
    // Only process dept-level stakeholders — client-level ones are legacy and no longer used
    const shs = (client.stakeholders || []).filter(s => s.level === "dept" || newSHIds.has(s.id));
    if (!shs.length) { showToast("No stakeholders to save"); return; }

    // Pre-validate every row being saved — block the save (rather than silently
    // skip) so the user knows which row needs to be fixed. Validate the raw
    // firstName/lastName fields directly; don't fall back to splitting `name`,
    // because `name` is rebuilt from whichever side is filled — so a row with
    // only a last name produces name="Doe", which would falsely satisfy a
    // firstName-fallback check.
    const incomplete = shs.filter(s => {
      const fn = (s.firstName || "").trim();
      const ln = (s.lastName || "").trim();
      const em = (s.email || "").trim();
      return !fn || !ln || !em || !s.deptId;
    });
    if (incomplete.length) {
      const missing = (s) => {
        const parts = [];
        if (!(s.firstName || "").trim()) parts.push("first name");
        if (!(s.lastName || "").trim()) parts.push("last name");
        if (!(s.email || "").trim()) parts.push("email");
        if (!s.deptId) parts.push("department");
        return parts.join(", ");
      };
      const first = incomplete[0];
      const who = [(first.firstName || "").trim(), (first.lastName || "").trim()].filter(Boolean).join(" ").trim();
      const label = who || (first.email || "").trim() || "the empty row";
      const msg = incomplete.length === 1
        ? `Please fill in the stakeholder fields for ${label}: ${missing(first)}`
        : `${incomplete.length} stakeholder rows are incomplete — fill in or remove them before saving`;
      showToast(msg, "error");
      return;
    }
    // Email format check on every row
    const badEmail = shs.find(s => s.email && !EMAIL_REGEX.test(s.email.trim()));
    if (badEmail) {
      showToast(`Stakeholder email "${badEmail.email}" is not a valid email address`, "error");
      return;
    }

    // Duplicate email within the SAME department — block save. Two
    // stakeholders sharing the same email in the same dept would receive
    // duplicate review requests and the OTP login flow can't disambiguate
    // which row to log into.
    {
      const seenByDept = new Map(); // key: `${deptId}|${emailLower}` → first stakeholder seen
      for (const s of shs) {
        const em = (s.email || "").trim().toLowerCase();
        if (!em || !s.deptId) continue;
        const key = `${s.deptId}|${em}`;
        if (seenByDept.has(key)) {
          const dept = (client.departments || []).find(d => d.id === s.deptId);
          const deptName = dept ? dept.name : "this department";
          showToast(`Duplicate email "${s.email}" in ${deptName} — each stakeholder in a department must have a unique email.`, "error");
          return;
        }
        seenByDept.set(key, s);
      }
    }

    let failed = 0;
    // Sequential — not Promise.all — to prevent race condition in backend ID generation
    // (parallel POSTs both read maxNum=0 → both generate S1 → duplicate key error)
    for (const s of shs) {
      try {
        const firstName = s.firstName?.trim() || s.name?.split(" ")[0]?.trim() || "";
        const lastName = s.lastName?.trim() || s.name?.split(" ").slice(1).join(" ")?.trim() || "";
        const email = s.email?.trim() || "";
        if (newSHIds.has(s.id)) {
          // Defensive — should never trigger now that we pre-validate above.
          if (!firstName || !lastName || !email || !s.deptId) { failed++; continue; }
          const res = await apiFetch(`/api/clients/${selId}/stakeholders`, {
            method: "POST",
            body: JSON.stringify({ first_name: firstName, last_name: lastName, email, designation: s.designation || "", level: "dept", dept_id: s.deptId }),
          });
          const d = await res.json();
          if (d.success) {
            const realId = String(d.id);
            setClients(cs => cs.map(c => c.id === selId
              ? { ...c, stakeholders: (c.stakeholders || []).map(sh => sh.id === s.id ? { ...sh, id: realId } : sh) }
              : c));
            setNewSHIds(prev => { const n = new Set(prev); n.delete(s.id); return n; });
          } else { failed++; if (d.message) { showToast(d.message, "error"); return; } }
        } else {
          const res = await apiFetch(`/api/clients/${selId}/stakeholders/${s.id}`, {
            method: "PUT",
            body: JSON.stringify({ first_name: firstName || undefined, last_name: lastName || undefined, email: email || undefined, designation: s.designation || "", level: "dept", dept_id: s.deptId || null, active: s.active ? 1 : 0 }),
          });
          const d = await res.json();
          if (!d.success) { failed++; if (d.message) { showToast(d.message, "error"); return; } }
        }
      } catch (e) { failed++; }
    }
    showToast(failed ? `${failed} stakeholder(s) failed to save` : "Stakeholders saved", failed ? "error" : "");
  };

  // ── Client fields ─────────────────────────────────────────────────────────────
  const updateStat = v => setClients(cs => cs.map(c => c.id === selId ? { ...c, status: v } : c));
  const updatePC = (fld, v) => setClients(cs => cs.map(c => {
    if (c.id !== selId) return c;
    const updated = { ...(c.primaryContact || {}), [fld]: v };
    if (fld === "firstName" || fld === "lastName") {
      updated.name = [updated.firstName || "", updated.lastName || ""].filter(Boolean).join(" ");
    }
    return { ...c, primaryContact: updated };
  }));
  const updateField = (fld, v) => setClients(cs => cs.map(c => c.id === selId ? { ...c, [fld]: v } : c));
  const updateComm = (fld, v) => setClients(cs => cs.map(c => c.id === selId ? { ...c, communication: { ...(c.communication || {}), [fld]: v } } : c));
  const updateAddr = (fld, v) => setClients(cs => cs.map(c => c.id === selId ? { ...c, address: { ...(c.address || {}), [fld]: v } } : c));
  const updateBilling = (fld, v) => setClients(cs => cs.map(c => c.id === selId ? { ...c, billingAddress: { ...(c.billingAddress || {}), [fld]: v } } : c));

  // ── Additional Contacts helpers ───────────────────────────────────────────────
  const [contactsEditMode, setContactsEditMode] = useState(false);
  const updateContact = (cid, fld, v) => setClients(cs => cs.map(cl => {
    if (cl.id !== selId) return cl;
    return {
      ...cl, contacts: (cl.contacts || []).map(ct => {
        if (ct.id !== cid) return ct;
        const updated = { ...ct, [fld]: v };
        if (fld === "firstName" || fld === "lastName") {
          updated.name = [updated.firstName || "", updated.lastName || ""].filter(Boolean).join(" ");
        }
        return updated;
      })
    };
  }));
  const addNewContact = () => {
    const tempId = "NEW_" + Date.now();
    setClients(cs => cs.map(cl =>
      cl.id === selId ? { ...cl, contacts: [...(cl.contacts || []), { id: tempId, firstName: "", lastName: "", name: "", type: "Operations", designation: "", department: "", email: "", phone: "", mobile: "", _isNew: true }] } : cl
    ));
  };
  const removeContactLocally = (cid) => setClients(cs => cs.map(cl =>
    cl.id === selId ? { ...cl, contacts: (cl.contacts || []).filter(ct => ct.id !== cid) } : cl
  ));
  const saveContacts = async () => {
    const contacts = client.contacts || [];
    let failed = 0;
    for (const ct of contacts) {
      if (!ct.firstName?.trim() && !ct.lastName?.trim() && !ct.name?.trim()) continue;
      try {
        if (ct._isNew) {
          const res = await apiFetch(`/api/clients/${selId}/contacts`, {
            method: "POST",
            body: JSON.stringify({ first_name: ct.firstName || ct.name?.split(" ")[0] || "", last_name: ct.lastName || ct.name?.split(" ").slice(1).join(" ") || "", type: ct.type, designation: ct.designation, department: ct.department, email: ct.email, phone: ct.phone, mobile: ct.mobile }),
          });
          const d = await res.json();
          if (d.success) {
            setClients(cs => cs.map(cl =>
              cl.id === selId ? { ...cl, contacts: (cl.contacts || []).map(x => x.id === ct.id ? { ...x, id: d.id, _isNew: false } : x) } : cl
            ));
          } else { failed++; }
        } else {
          const res = await apiFetch(`/api/clients/${selId}/contacts/${ct.id}`, {
            method: "PUT",
            body: JSON.stringify({ first_name: ct.firstName || ct.name?.split(" ")[0] || "", last_name: ct.lastName || ct.name?.split(" ").slice(1).join(" ") || "", type: ct.type, designation: ct.designation, department: ct.department, email: ct.email, phone: ct.phone, mobile: ct.mobile }),
          });
          const d = await res.json();
          if (!d.success) failed++;
        }
      } catch (e) { failed++; }
    }
    if (failed === 0) { setContactsEditMode(false); showToast("Contacts saved"); }
    else showToast(`${failed} contact(s) failed to save`, "error");
  };
  const deleteContact = async (cid) => {
    if (cid.toString().startsWith("NEW_")) { removeContactLocally(cid); return; }
    removeContactLocally(cid);
    try { await apiFetch(`/api/clients/${selId}/contacts/${cid}`, { method: "DELETE" }); }
    catch (e) { showToast("Failed to delete contact", "error"); }
  };

  // ── Excel Export ─────────────────────────────────────────────────────────────
  // Downloads every client in the same column layout as the bulk-import template,
  // so the file is round-trippable: download → edit in Excel → re-upload via
  // the same Import button to bulk-update.
  const [exportLoading, setExportLoading] = useState(false);
  const downloadClientsExport = async () => {
    if (exportLoading) return;
    if (!clients.length) { showToast("No clients to export", "error"); return; }
    setExportLoading(true);
    showToast("Generating Excel…", "");
    try {
      const res = await apiFetch("/api/clients/export");
      if (!res.ok) { showToast("Export failed — please try again", "error"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Clients_Export_${todayIST()}.xlsx`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      showToast(`Exported ${clients.length} client${clients.length !== 1 ? "s" : ""}`, "#10B981");
    } catch (err) {
      console.error("Client export error:", err);
      showToast("Export failed — please try again", "error");
    } finally {
      setExportLoading(false);
    }
  };

  // ── PDF Export ───────────────────────────────────────────────────────────────
  // Loads jsPDF + jspdf-autotable from CDN once (mirrors EmployeeDatabase).
  const loadJsPDF = () => new Promise((resolve, reject) => {
    if (window.jspdf?.jsPDF) { resolve(window.jspdf.jsPDF); return; }
    const s1 = document.createElement("script");
    s1.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    s1.onload = () => {
      const s2 = document.createElement("script");
      s2.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js";
      s2.onload = () => resolve(window.jspdf.jsPDF);
      s2.onerror = reject;
      document.head.appendChild(s2);
    };
    s1.onerror = reject;
    document.head.appendChild(s1);
  });

  const [pdfLoading, setPdfLoading] = useState(false);
  const downloadClientsPDF = async () => {
    if (pdfLoading) return;
    if (!clients.length) { showToast("No clients to export", "error"); return; }
    setPdfLoading(true);
    showToast("Generating PDF…", "");
    try {
      const JsPDF = await loadJsPDF();
      const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

      // Header bar — same orange band as the employee PDF for visual consistency.
      doc.setFillColor(232, 82, 10);
      doc.rect(0, 0, 297, 16, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Client Database Export", 10, 10.5);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(`${clients.length} client${clients.length !== 1 ? "s" : ""}  ·  Generated: ${new Date().toLocaleString()}`, 10, 14.5);

      // Status pill colours — mirror the client status enum used elsewhere.
      const statusColour = s => ({
        active:     { r: 209, g: 250, b: 229 },
        onboarding: { r: 254, g: 243, b: 199 },
        inactive:   { r: 241, g: 245, b: 249 },
      }[String(s || "").toLowerCase()] || { r: 241, g: 245, b: 249 });
      const statusTextColour = s => ({
        active:     [6, 95, 70],
        onboarding: [146, 64, 14],
        inactive:   [100, 116, 139],
      }[String(s || "").toLowerCase()] || [100, 116, 139]);

      const headStyles = { fillColor: [232, 82, 10], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5 };
      const bodyStyles = { fontSize: 7.5, textColor: [13, 27, 42] };
      const altRow = { fillColor: [248, 250, 252] };
      const margin = { left: 8, right: 8 };

      const sectionLabel = (label, y) => {
        doc.setFillColor(13, 27, 42);
        doc.rect(8, y, 281, 7, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text(label, 12, y + 5);
      };

      // Section 1 — Identity & Status
      sectionLabel("Section 1 — Identity & Status", 20);
      doc.autoTable({
        startY: 29,
        head: [["Code", "Name", "Industry", "Company Type", "Status", "Primary Domain", "Website", "Founded", "Emp Range"]],
        body: clients.map(c => [
          c.code || "",
          c.name || "",
          c.industry || "",
          c.companyType || c.company_type || "",
          (c.status || "").toString(),
          c.domain || c.primary_domain || "",
          c.website || "",
          c.founded || c.founded_year || "",
          c.empRange || c.emp_range || "",
        ]),
        headStyles,
        bodyStyles,
        alternateRowStyles: altRow,
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 38, fontStyle: "bold" },
          2: { cellWidth: 26 },
          3: { cellWidth: 28 },
          4: { cellWidth: 24 },
          5: { cellWidth: 40 },
          6: { cellWidth: 42, overflow: "linebreak" },
          7: { cellWidth: 18 },
          8: { cellWidth: 22 },
        },
        willDrawCell: (data) => {
          if (data.section === "body" && data.column.index === 4) data.cell.text = [];
        },
        didDrawCell: (data) => {
          if (data.section === "body" && data.column.index === 4) {
            const status = String(data.cell.raw || "");
            const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : "—";
            const { r, g, b } = statusColour(status);
            const [tr, tg, tb] = statusTextColour(status);
            const { x, y, width: w, height: h } = data.cell;
            doc.setFillColor(r, g, b);
            doc.roundedRect(x + 1, y + 1.5, w - 2, h - 3, 2, 2, "F");
            doc.setTextColor(tr, tg, tb);
            doc.setFontSize(6.5);
            doc.setFont("helvetica", "bold");
            doc.text(label, x + w / 2, y + h / 2 + 0.5, { align: "center", baseline: "middle" });
          }
        },
        margin,
      });

      // Section 2 — Primary Contact
      const sec2Y = doc.lastAutoTable.finalY + 6;
      sectionLabel("Section 2 — Primary Contact", sec2Y);
      doc.autoTable({
        startY: sec2Y + 9,
        head: [["Code", "Client", "First Name", "Last Name", "Email", "Phone", "Mobile", "Designation", "Department"]],
        body: clients.map(c => {
          const pc = c.primaryContact || {};
          return [
            c.code || "",
            c.name || "",
            pc.firstName || c.pc_first_name || "",
            pc.lastName || c.pc_last_name || "",
            pc.email || c.pc_email || "",
            pc.phone || c.pc_phone || "",
            pc.mobile || c.pc_mobile || "",
            pc.designation || c.pc_designation || "",
            pc.department || c.pc_department || "",
          ];
        }),
        headStyles,
        bodyStyles,
        alternateRowStyles: altRow,
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 34, fontStyle: "bold" },
          2: { cellWidth: 22 },
          3: { cellWidth: 22 },
          4: { cellWidth: 50, overflow: "linebreak" },
          5: { cellWidth: 26 },
          6: { cellWidth: 26 },
          7: { cellWidth: 32 },
          8: { cellWidth: 26 },
        },
        margin,
      });

      // Section 3 — Stakeholders (one row per stakeholder; clients with none
      // contribute zero rows).
      const stakeRows = [];
      for (const c of clients) {
        const list = (c.stakeholders || []).filter(s => s && (s.firstName || s.lastName || s.email || s.first_name || s.last_name));
        for (const s of list) {
          const deptName = s.deptName || s.dept_name || (() => {
            const id = s.deptId ?? s.dept_id;
            const dept = id ? (c.departments || []).find(d => d.id === id) : null;
            return dept ? dept.name : "";
          })();
          stakeRows.push([
            c.code || "",
            c.name || "",
            s.firstName || s.first_name || "",
            s.lastName || s.last_name || "",
            s.email || "",
            s.designation || "",
            deptName || "",
          ]);
        }
      }
      if (stakeRows.length > 0) {
        const sec3Y = doc.lastAutoTable.finalY + 6;
        sectionLabel(`Section 3 — Stakeholders (${stakeRows.length})`, sec3Y);
        doc.autoTable({
          startY: sec3Y + 9,
          head: [["Code", "Client", "First Name", "Last Name", "Email", "Designation", "Department"]],
          body: stakeRows,
          headStyles,
          bodyStyles,
          alternateRowStyles: altRow,
          columnStyles: {
            0: { cellWidth: 20 },
            1: { cellWidth: 36, fontStyle: "bold" },
            2: { cellWidth: 24 },
            3: { cellWidth: 24 },
            4: { cellWidth: 54, overflow: "linebreak" },
            5: { cellWidth: 36 },
            6: { cellWidth: 28 },
          },
          margin,
        });
      }

      // Page-number footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setTextColor(148, 163, 184);
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.text(`Page ${i} of ${pageCount}  ·  Dolluz Corp EPR`, 297 / 2, 207, { align: "center" });
      }

      applyPdfWatermark(doc, await loadWatermarkDataUrl());
      doc.save(`Clients_Export_${todayIST()}.pdf`);
      showToast(`PDF downloaded — ${clients.length} client${clients.length !== 1 ? "s" : ""}`, "#10B981");
    } catch (err) {
      console.error("Client PDF export error:", err);
      showToast("PDF generation failed — please try again", "error");
    } finally {
      setPdfLoading(false);
    }
  };

  // ── Excel Import ─────────────────────────────────────────────────────────────
  const downloadClientTemplate = async () => {
    if (templateLoading) return;
    setTemplateLoading(true);
    showToast("Preparing template…", "");
    try {
      const res = await apiFetch("/api/clients/template");
      if (!res.ok) { showToast("Template download failed — please try again", "error"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "Client_Import_Template.xlsx";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      showToast("Template downloaded — orange = required, ▼ columns have dropdowns", "#10B981");
    } catch (err) {
      console.error("Client template download error:", err);
      showToast("Template download failed — please try again", "error");
    } finally {
      setTemplateLoading(false);
    }
  };

  const runClientImport = async () => {
    if (!importFile) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const res = await apiFetch("/api/clients/import", { method: "POST", body: fd, headers: {} });
      const d = await res.json();
      setImportResult(d);
      if (d.success && d.inserted + d.updated > 0) {
        // Reload clients list
        const lr = await apiFetch("/api/clients");
        const ld = await lr.json();
        if (ld.success) setClients(ld.data.map(c => normalizeClient(c)));

        // The list endpoint only returns a small subset of columns (basic
        // identity + status). The currently-open client's detail panel reads
        // extended fields (legal_name, website, founded_year, addresses, etc.)
        // that are NOT in the list response. Without this follow-up fetch,
        // those fields would render blank until the user manually reloads or
        // re-selects the client.
        if (selId) {
          try {
            const dr = await apiFetch(`/api/clients/${selId}`);
            const dd = await dr.json();
            if (dd.success && dd.data) {
              setClients(cs => cs.map(c => c.id === selId ? normalizeClient(dd.data, c) : c));
            }
          } catch (e) { /* non-fatal — user can still reload manually */ }
        }
      }
    } catch (e) { setImportResult({ success: false, message: "Network error during import" }); }
    setImportLoading(false);
  };

  const saveClient = async () => {
    try {
      const res = await apiFetch(`/api/clients/${selId}`, {
        method: "PUT",
        body: JSON.stringify({
          name: client.name,
          legal_name: client.legalName || null,
          company_type: client.companyType || null,
          reg_number: client.regNumber || null,
          industry: client.industry || null,
          status: client.status || "active",
          color_hex: client.color_hex || null,
          primary_domain: client.domain || null,
          website: client.website || null,
          linkedin: client.linkedin || null,
          founded_year: client.founded ? parseInt(client.founded) : null,
          emp_range: client.empRange || null,
          revenue_range: client.revenueRange || null,
          // Telephone & Fax
          landline1: client.communication?.landline || null,
          landline1_extn: client.communication?.landlineExtn || null,
          landline2: client.communication?.landline2 || null,
          landline2_extn: client.communication?.landline2Extn || null,
          fax1: client.communication?.fax || null,
          fax2: client.communication?.fax2 || null,
          // Shared emails
          email_reception: client.communication?.receptionEmail || null,
          email_finance: client.communication?.financeEmail || null,
          email_hr: client.communication?.hrEmail || null,
          email_support: client.communication?.supportEmail || null,
          // Address
          addr_line1: client.address?.line1 || null,
          addr_line2: client.address?.line2 || null,
          addr_city: client.address?.city || null,
          addr_state: client.address?.state || null,
          addr_pin: client.address?.pin || null,
          addr_country: client.address?.country || null,
          // Billing address
          billing_same: client.billingSame ? 1 : 0,
          billing_line1: client.billingAddress?.line1 || null,
          billing_line2: client.billingAddress?.line2 || null,
          billing_city: client.billingAddress?.city || null,
          billing_state: client.billingAddress?.state || null,
          billing_pin: client.billingAddress?.pin || null,
          billing_country: client.billingAddress?.country || null,
          // Primary contact
          pc_first_name: client.primaryContact?.firstName || null,
          pc_last_name: client.primaryContact?.lastName || null,
          pc_email: client.primaryContact?.email || null,
          pc_phone: client.primaryContact?.phone || null,
          pc_designation: client.primaryContact?.designation || null,
          pc_department: client.primaryContact?.department || null,
          pc_mobile: client.primaryContact?.mobile || null,
          notes: client.notes || null,
        }),
      });
      const d = await res.json();
      if (d.success) { setEditMode(false); showToast("Client saved"); }
      else showToast("Error: " + (d.message || "Save failed"));
    } catch (e) { showToast("Network error — could not save client", "error"); }
  };

  // ── Domains ───────────────────────────────────────────────────────────────────
  const addDomain = async () => {
    if (!newDomain.trim()) return;
    const val = newDomain.trim();
    setNewDomain("");
    try {
      const res = await apiFetch(`/api/clients/${selId}/domains`, { method: "POST", body: JSON.stringify({ domain: val }) });
      const d = await res.json();
      if (d.success) {
        setClients(cs => cs.map(c => c.id === selId ? { ...c, domains: [...(c.domains || []), { id: d.id, domain: d.domain }] } : c));
        showToast("Domain added");
      } else { showToast("Error: " + (d.message || "Failed to add domain")); }
    } catch (e) { showToast("Network error", "error"); }
  };
  const remDomain = async (domainId) => {
    // #2 — block removal if this is the last domain (frontend guard + backend re-checks)
    if ((client.domains || []).length <= 1) {
      showToast("At least one domain is required. Add another before removing this one.", "error");
      return;
    }
    const found = (client.domains || []).find(d => (d.id || d) === domainId);
    setConfirmDelDomain({ id: domainId, domain: found?.domain || domainId });
  };
  const doRemDomain = async (domainId) => {
    setConfirmDelDomain(null);
    // Optimistic remove — restore if backend rejects (e.g. last-item rule).
    const prev = client.domains || [];
    setClients(cs => cs.map(c => c.id === selId ? { ...c, domains: prev.filter(x => (x.id || x) !== domainId) } : c));
    try {
      const r = await apiFetch(`/api/clients/${selId}/domains/${domainId}`, { method: "DELETE" });
      const d = await r.json();
      if (!d.success) {
        setClients(cs => cs.map(c => c.id === selId ? { ...c, domains: prev } : c));
        showToast(d.message || "Failed to remove domain", "error");
      } else { showToast("Domain removed"); }
    } catch (e) {
      setClients(cs => cs.map(c => c.id === selId ? { ...c, domains: prev } : c));
      showToast("Failed to remove domain", "error");
    }
  };
  // #3 — edit existing domain (rename)
  const editDomain = async (domainId, newValue) => {
    const cleaned = String(newValue || "").trim().toLowerCase();
    if (!cleaned) return;
    if (!DOMAIN_REGEX.test(cleaned)) { showToast("Invalid domain format", "error"); return; }
    const prev = client.domains || [];
    setClients(cs => cs.map(c => c.id === selId ? { ...c, domains: prev.map(x => (x.id || x) === domainId ? { ...x, domain: cleaned } : x) } : c));
    try {
      const r = await apiFetch(`/api/clients/${selId}/domains/${domainId}`, { method: "PUT", body: JSON.stringify({ domain: cleaned }) });
      const d = await r.json();
      if (!d.success) {
        setClients(cs => cs.map(c => c.id === selId ? { ...c, domains: prev } : c));
        showToast(d.message || "Failed to update domain", "error");
      } else { showToast("Domain updated"); }
    } catch (e) {
      setClients(cs => cs.map(c => c.id === selId ? { ...c, domains: prev } : c));
      showToast("Network error", "error");
    }
  };

  // ── Departments ───────────────────────────────────────────────────────────────
  const addDept = async () => {
    if (!newDept.trim()) return;
    const val = newDept.trim();
    setNewDept("");
    try {
      const res = await apiFetch(`/api/clients/${selId}/departments`, { method: "POST", body: JSON.stringify({ name: val }) });
      const d = await res.json();
      if (d.success) {
        setClients(cs => cs.map(c => c.id === selId ? { ...c, departments: [...(c.departments || []), { id: d.id, name: d.name }] } : c));
        showToast("Department added");
      } else { showToast("Error: " + (d.message || "Failed to add department")); }
    } catch (e) { showToast("Network error", "error"); }
  };
  const remDept = async (deptId) => {
    // #2 — frontend guard for last-item; backend re-checks regardless.
    if ((client.departments || []).length <= 1) {
      showToast("At least one department is required. Add another before removing this one.", "error");
      return;
    }
    const prevDepts = client.departments || [];
    const prevStakeholders = client.stakeholders || [];
    const prevPrimary = client.primaryStakeholderId;
    setClients(cs => cs.map(c => c.id === selId ? {
      ...c,
      departments: prevDepts.filter(d => d.id !== deptId),
      stakeholders: prevStakeholders.filter(s => s.deptId !== deptId),
      primaryStakeholderId: prevStakeholders.some(s => s.deptId === deptId && s.id === prevPrimary) ? null : prevPrimary,
    } : c));
    try {
      const r = await apiFetch(`/api/clients/${selId}/departments/${deptId}`, { method: "DELETE" });
      const d = await r.json();
      if (!d.success) {
        setClients(cs => cs.map(c => c.id === selId ? { ...c, departments: prevDepts, stakeholders: prevStakeholders, primaryStakeholderId: prevPrimary } : c));
        showToast(d.message || "Failed to remove department", "error");
      }
    } catch (e) {
      setClients(cs => cs.map(c => c.id === selId ? { ...c, departments: prevDepts, stakeholders: prevStakeholders, primaryStakeholderId: prevPrimary } : c));
      showToast("Failed to remove department", "error");
    }
  };
  // #3 — edit existing department (rename)
  const editDept = async (deptId, newName) => {
    const cleaned = String(newName || "").trim();
    if (!cleaned) return;
    const prev = client.departments || [];
    setClients(cs => cs.map(c => c.id === selId ? { ...c, departments: prev.map(d => d.id === deptId ? { ...d, name: cleaned } : d) } : c));
    try {
      const r = await apiFetch(`/api/clients/${selId}/departments/${deptId}`, { method: "PUT", body: JSON.stringify({ name: cleaned }) });
      const d = await r.json();
      if (!d.success) {
        setClients(cs => cs.map(c => c.id === selId ? { ...c, departments: prev } : c));
        showToast(d.message || "Failed to update department", "error");
      } else { showToast("Department updated"); }
    } catch (e) {
      setClients(cs => cs.map(c => c.id === selId ? { ...c, departments: prev } : c));
      showToast("Network error", "error");
    }
  };
  const deleteClient = async () => {
    const deletedName = client.name;
    setConfirmDel(false);
    try {
      const res = await apiFetch(`/api/clients/${selId}`, { method: "DELETE" });
      const d = await res.json();
      if (d.success) {
        const rem = clients.filter(c => c.id !== selId);
        setClients(rem);
        setSelIdPersist(rem[0] ? rem[0].id : "");
        showToast(`"${deletedName}" marked as Inactive`);
      } else {
        showToast("Cannot delete: " + (d.message || "Server error"), "error");
      }
    } catch (e) {
      showToast("Network error — client not deleted", "error");
    }
  };
  const addClient = async (nc) => {
    try {
      const res = await apiFetch("/api/clients", {
        method: "POST",
        body: JSON.stringify({
          // Send `code` only if the user manually typed one — otherwise backend
          // auto-generates the next available CL number (fills gaps).
          ...(nc.codeManual && nc.code ? { code: nc.code } : {}),
          name: nc.name,
          primary_domain: nc.domain || null,
          legal_name: nc.legalName || null,
          company_type: nc.companyType || null,
          reg_number: nc.regNumber || null,
          industry: nc.industry || null,
          status: nc.status || "onboarding",
          // Auto-generated brand colour — deterministic hash of client code so
          // each client gets a unique, repeatable colour across the app.
          // When auto-generated, the backend will re-derive this from the
          // assigned code; the value here is just a sensible default.
          color_hex: autoColorForCode(nc.code || "DEFAULT"),
          website: nc.website || null,
          linkedin: nc.linkedin || null,
          founded_year: nc.founded ? parseInt(nc.founded) : null,
          emp_range: nc.empRange || null,
          revenue_range: nc.revenueRange || null,
          // Telephone & Fax
          landline1: nc.communication?.landline || null,
          landline1_extn: nc.communication?.landlineExtn || null,
          landline2: nc.communication?.landline2 || null,
          landline2_extn: nc.communication?.landline2Extn || null,
          fax1: nc.communication?.fax || null,
          fax2: nc.communication?.fax2 || null,
          // Shared emails
          email_reception: nc.communication?.receptionEmail || null,
          email_finance: nc.communication?.financeEmail || null,
          email_hr: nc.communication?.hrEmail || null,
          email_support: nc.communication?.supportEmail || null,
          // Office address
          addr_line1: nc.address?.line1 || null,
          addr_line2: nc.address?.line2 || null,
          addr_city: nc.address?.city || null,
          addr_state: nc.address?.state || null,
          addr_pin: nc.address?.pin || null,
          addr_country: nc.address?.country || "India",
          // Billing address
          billing_same: nc.billingAddress?.line1 === nc.address?.line1 ? 1 : 0,
          billing_line1: nc.billingAddress?.line1 || null,
          billing_line2: nc.billingAddress?.line2 || null,
          billing_city: nc.billingAddress?.city || null,
          billing_state: nc.billingAddress?.state || null,
          billing_pin: nc.billingAddress?.pin || null,
          billing_country: nc.billingAddress?.country || "India",
          // Primary contact
          pc_first_name: nc.primaryContact?.firstName || null,
          pc_last_name: nc.primaryContact?.lastName || null,
          pc_email: nc.primaryContact?.email || null,
          pc_phone: nc.primaryContact?.phone || null,
          pc_designation: nc.primaryContact?.designation || null,
          pc_department: nc.primaryContact?.department || null,
          pc_mobile: nc.primaryContact?.mobile || null,
          notes: nc.notes || null,
        }),
      });
      const d = await res.json();
      if (d.success) {
        // Backend returns the assigned id/code — trust it over whatever the modal previewed.
        const clientId = d.id || d.data?.id || nc.code;
        const assignedCode = d.code || d.id || nc.code;
        // Save domain to DB
        let savedDomains = [];
        if (nc.domain) {
          try {
            const dr = await apiFetch(`/api/clients/${clientId}/domains`, { method: "POST", body: JSON.stringify({ domain: nc.domain }) });
            const dd = await dr.json();
            if (dd.success) savedDomains = [{ id: dd.id, domain: dd.domain }];
          } catch (e) { }
        }
        // Save first department to DB
        let savedDepts = [];
        if (nc.departments && nc.departments[0]?.name) {
          try {
            const dr = await apiFetch(`/api/clients/${clientId}/departments`, { method: "POST", body: JSON.stringify({ name: nc.departments[0].name }) });
            const dd = await dr.json();
            if (dd.success) savedDepts = [{ id: dd.id, name: dd.name }];
          } catch (e) { }
        }
        // Save additional contacts to DB
        let savedContacts = [];
        if (nc.additionalContacts && nc.additionalContacts.length > 0) {
          for (const ct of nc.additionalContacts) {
            if (!ct.firstName?.trim() && !ct.lastName?.trim() && !ct.name?.trim()) continue;
            try {
              const cr = await apiFetch(`/api/clients/${clientId}/contacts`, {
                method: "POST",
                body: JSON.stringify({
                  // Backend requires first_name + last_name — not the legacy concatenated `name`.
                  first_name: ct.firstName || (ct.name ? ct.name.split(" ")[0] : "") || "",
                  last_name: ct.lastName || (ct.name ? ct.name.split(" ").slice(1).join(" ") : "") || "",
                  type: ct.type || "Operations",
                  designation: ct.designation || null, department: ct.department || null,
                  email: ct.email || null, phone: ct.phone || null, mobile: ct.mobile || null,
                }),
              });
              const cd = await cr.json();
              if (cd.success) savedContacts.push({ ...ct, id: cd.id });
            } catch (e) { /* silent fail per contact */ }
          }
        }
        const saved = { ...nc, id: clientId, code: assignedCode, domains: savedDomains, departments: savedDepts, stakeholders: [], contacts: savedContacts };
        setClients(p => [...p, saved]);
        setSelIdPersist(clientId);
        setShowAdd(false);
        showToast("Client created");
      } else {
        showToast(d.message || "Failed to save client", "error");
      }
    } catch (e) {
      showToast("Network error — could not save client", "error");
    }
  };

  if (!client) return (
    <div className="fade-in">
      <TopBar title="Client Configuration" subtitle="Manage clients, departments, authorised domains and stakeholder assignments" {...topBarProps} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 300, gap: 16 }}>
        <div style={{ fontSize: 32 }}>🏢</div>
        <div style={{ color: "#94A3B8", fontSize: 14, fontWeight: 500 }}>No clients yet. Add your first client to get started.</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn-primary" style={{ fontSize: 13 }} onClick={() => setShowAdd(true)}>+ Add Client</button>
          <button className="btn-secondary" style={{ fontSize: 13 }} onClick={() => setShowImport(true)}>⬆ Import Excel</button>
          <button className="btn-secondary" style={{ fontSize: 13, color: "#10B981", borderColor: "#A7F3D0" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#10B981"; e.currentTarget.style.background = "#F0FDF4"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#A7F3D0"; e.currentTarget.style.background = "#fff"; }}
            onClick={downloadClientsExport} disabled={exportLoading} title={!clients.length ? "No clients to export" : "Download all clients (re-importable)"}>{exportLoading ? "Preparing…" : "📊 Export XLS"}</button>
          <button className="btn-secondary" style={{ fontSize: 13, color: "#EF4444", borderColor: "#FECACA" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#EF4444"; e.currentTarget.style.background = "#FEF2F2"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#FECACA"; e.currentTarget.style.background = "#fff"; }}
            onClick={downloadClientsPDF} disabled={pdfLoading} title={!clients.length ? "No clients to export" : "Download all clients as PDF"}>{pdfLoading ? "Preparing…" : "📄 Export PDF"}</button>
        </div>
      </div>
      {showAdd && <AddClientModal onAdd={addClient} onClose={() => setShowAdd(false)} visible={showAdd} existingClients={clients} />}
      {showImport && <ClientImportModal onClose={() => { setShowImport(false); setImportFile(null); setImportResult(null); }} importFile={importFile} setImportFile={setImportFile} onImport={runClientImport} loading={importLoading} result={importResult} onDownloadTemplate={downloadClientTemplate} templateLoading={templateLoading} />}
      {toast && <Toast msg={toast} type={toastType} />}
    </div>
  );

  return (
    <div className="fade-in">
      <TopBar title="Client Configuration" subtitle="Manage clients, departments, authorised domains and stakeholder assignments" {...topBarProps} />
      <div style={{ display: "grid", gridTemplateColumns: "230px 1fr", gap: 16, alignItems: "start" }}>

        <div className="card" style={{ padding: "6px 8px" }}>
          <div style={{ padding: "10px 10px 6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8 }}>
              Clients {filtersActive && <span style={{ color: "#E8520A" }}>· {filteredClients.length}/{clients.length}</span>}
            </span>
            {filtersActive && (
              <button onClick={clearAllFilters}
                title="Clear all filters"
                style={{ background: "none", border: "none", color: "#E8520A", fontSize: 10, fontWeight: 700, cursor: "pointer", padding: "2px 6px" }}>
                Clear
              </button>
            )}
          </div>

          {/* ── Filter panel ─────────────────────────────────────────── */}
          <div style={{ padding: "4px 6px 10px", borderBottom: "1px solid #F1F5F9", marginBottom: 6 }}>
            {/* Status chips */}
            <div style={{ fontSize: 9, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4, padding: "0 4px" }}>Status</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
              {[
                ["active", "#10B981", "#F0FDF4"],
                ["onboarding", "#F59E0B", "#FFFBEB"],
                ["inactive", "#94A3B8", "#F8FAFC"],
              ].map(([s, fg, bg]) => {
                const on = statusFilter.has(s);
                return (
                  <button key={s} onClick={() => toggleStatusFilter(s)}
                    style={{
                      fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 100,
                      border: "1px solid " + (on ? fg : "#E2E8F0"),
                      background: on ? fg : bg, color: on ? "#fff" : fg,
                      cursor: "pointer", textTransform: "capitalize", letterSpacing: 0.2,
                    }}>
                    {s}
                  </button>
                );
              })}
            </div>

            {/* Allocation chips */}
            <div style={{ fontSize: 9, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4, padding: "0 4px" }}>Employees</div>
            <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
              {[
                ["", "All"],
                ["with", "With"],
                ["without", "Without"],
              ].map(([v, l]) => {
                const on = allocFilter === v;
                return (
                  <button key={v || "all"} onClick={() => setAllocFilter(v)}
                    style={{
                      flex: 1, fontSize: 10, fontWeight: 700, padding: "3px 0", borderRadius: 6,
                      border: "1px solid " + (on ? "#0D1B2A" : "#E2E8F0"),
                      background: on ? "#0D1B2A" : "#fff", color: on ? "#fff" : "#64748B",
                      cursor: "pointer",
                    }}>
                    {l}
                  </button>
                );
              })}
            </div>

            {/* Stakeholder chips */}
            <div style={{ fontSize: 9, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4, padding: "0 4px" }}>Stakeholders</div>
            <div style={{ display: "flex", gap: 4 }}>
              {[
                ["", "All"],
                ["with", "With"],
                ["without", "Without"],
              ].map(([v, l]) => {
                const on = shFilter === v;
                return (
                  <button key={v || "all"} onClick={() => setShFilter(v)}
                    style={{
                      flex: 1, fontSize: 10, fontWeight: 700, padding: "3px 0", borderRadius: 6,
                      border: "1px solid " + (on ? "#0D1B2A" : "#E2E8F0"),
                      background: on ? "#0D1B2A" : "#fff", color: on ? "#fff" : "#64748B",
                      cursor: "pointer",
                    }}>
                    {l}
                  </button>
                );
              })}
            </div>
          </div>

          {filteredClients.length === 0 && (
            <div style={{ padding: "20px 12px", textAlign: "center", color: "#94A3B8", fontSize: 11, fontStyle: "italic" }}>
              No clients match the current filters.
            </div>
          )}
          {filteredClients.map(c => (
            <div key={c.id} onClick={() => setSelIdPersist(c.id)}
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", borderRadius: 9, cursor: "pointer", marginBottom: 2,
                background: selId === c.id ? "#FFF5F0" : "transparent",
                borderLeft: selId === c.id ? "3px solid #E8520A" : "3px solid transparent", transition: "all .18s"
              }}>
              <Avatar name={c.name} size={30} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: selId === c.id ? "#E8520A" : "#1E293B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{(c.name || "").split(" ")[0]}</div>
                <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 1, display: "flex", gap: 6 }}>
                  <span>{(c.stakeholders || []).filter(s => s.active).length} SH</span>
                  <span>·</span>
                  <span style={{ color: empCount(c.id) > 0 ? "#10B981" : "#EF4444", fontWeight: 600 }}>{empCount(c.id)} emp</span>
                </div>
              </div>
              <Badge status={c.status} />
            </div>
          ))}
          <button className="btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: 8, fontSize: 12, padding: "8px 0" }} onClick={() => setShowAdd(true)}>+ Add Client</button>
          <button className="btn-secondary" style={{ width: "100%", justifyContent: "center", marginTop: 6, fontSize: 12, padding: "7px 0" }} onClick={() => setShowImport(true)}>⬆ Import Excel</button>
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <button className="btn-secondary" style={{ flex: 1, justifyContent: "center", fontSize: 12, padding: "7px 0", color: "#10B981", borderColor: "#A7F3D0" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#10B981"; e.currentTarget.style.background = "#F0FDF4"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#A7F3D0"; e.currentTarget.style.background = "#fff"; }}
              onClick={downloadClientsExport} disabled={exportLoading} title={!clients.length ? "No clients to export" : "Download all clients (re-importable)"}>{exportLoading ? "Preparing…" : "📊 Export XLS"}</button>
            <button className="btn-secondary" style={{ flex: 1, justifyContent: "center", fontSize: 12, padding: "7px 0", color: "#EF4444", borderColor: "#FECACA" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#EF4444"; e.currentTarget.style.background = "#FEF2F2"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#FECACA"; e.currentTarget.style.background = "#fff"; }}
              onClick={downloadClientsPDF} disabled={pdfLoading} title={!clients.length ? "No clients to export" : "Download all clients as PDF"}>{pdfLoading ? "Preparing…" : "📄 Export PDF"}</button>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="card" style={{ padding: "22px 26px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
              <Avatar name={client.name} size={48} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#0D1B2A", letterSpacing: -0.3 }}>{client.name}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5 }}>
                  {editMode ? (
                    <select value={client.status} onChange={e => updateStat(e.target.value)}
                      style={{ width: "auto", padding: "3px 10px", fontSize: 11, fontWeight: 700, borderRadius: 100, border: "1.5px solid #E2E8F0", background: "#F8FAFC", cursor: "pointer" }}>
                      <option value="active">active</option>
                      <option value="onboarding">onboarding</option>
                      <option value="inactive">inactive</option>
                    </select>
                  ) : (
                    <span style={{
                      padding: "3px 10px", fontSize: 11, fontWeight: 700, borderRadius: 100, border: "1.5px solid #E2E8F0", background: "#F8FAFC",
                      color: client.status === "active" ? "#10B981" : client.status === "inactive" ? "#EF4444" : "#F59E0B"
                    }}>
                      {client.status}
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: "#94A3B8" }}
                    title="Client Code cannot be updated — it is the primary key referenced by stakeholders, allocations, reviews, and email history.">
                    {client.code} <span style={{ fontSize: 9, opacity: 0.6 }}>(read-only)</span>
                  </span>
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#6366F1", background: "#EEF2FF", padding: "2px 8px", borderRadius: 6 }}>@{client.domain}</span>
                  <span style={{ fontSize: 11, color: "#10B981", background: "#F0FDF4", padding: "2px 8px", borderRadius: 6, fontWeight: 600 }}>{empCount(client.id)} employee{empCount(client.id) !== 1 ? "s" : ""}</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn-secondary" onClick={() => setEditMode(!editMode)} style={{ fontSize: 12 }}>{editMode ? "Cancel" : "Edit"}</button>
                {editMode && <button className="btn-primary" style={{ fontSize: 12 }} onClick={saveClient}>Save</button>}
                <button className="btn-danger" onClick={() => setConfirmDel(true)} style={{ padding: "7px 12px" }}>Delete</button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>Industry</label>
                {editMode
                  ? <select className="inp" value={client.industry || ""} onChange={e => updateField("industry", e.target.value)}>
                    <option value="">Select industry</option>
                    {["Healthcare", "Telecom", "Banking", "Automobile", "IT Services", "Finance", "Insurance", "Retail", "Manufacturing", "Pharma", "Education", "Other"].map(o => <option key={o}>{o}</option>)}
                  </select>
                  : <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{client.industry || "—"}</div>}
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>Primary Domain</label>
                {editMode
                  ? <input className="inp" value={client.domain || ""} maxLength={CLIENT_FIELD_MAX.domain}
                      onChange={e => updateField("domain", e.target.value.slice(0, CLIENT_FIELD_MAX.domain))}
                      style={{ borderColor: client.domain && !DOMAIN_REGEX.test(client.domain.trim()) ? "#EF4444" : "" }} />
                  : <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{client.domain || "—"}</div>}
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>Employees</label>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{empCount(client.id)} allocated</div>
              </div>
            </div>
            {/* Extended company fields — always visible, editable in Edit mode */}
            {/* #5 — show this section always, regardless of which fields have values */}
            {true && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #F1F5F9", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>

                {/* Text inputs */}
                {[
                  ["Legal / Registered Name", "legalName", "e.g. Infosys BPM Limited", CLIENT_FIELD_MAX.legalName],
                  ["Registration No.", "regNumber", "e.g. U72200MH2006", CLIENT_FIELD_MAX.regNumber],
                  ["Website", "website", "https://www.infosys.com", CLIENT_FIELD_MAX.website],
                  ["LinkedIn URL", "linkedin", "linkedin.com/company/...", CLIENT_FIELD_MAX.linkedin],
                ].map(([label, key, ph, maxLen]) => (
                  <div key={key}>
                    <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</label>
                    {editMode
                      ? <input className="inp" value={client[key] || ""} maxLength={maxLen}
                          onChange={e => updateField(key, e.target.value.slice(0, maxLen))} placeholder={ph} />
                      : <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{client[key] || "—"}</div>}
                  </div>
                ))}

                {/* Founded Year — digits only, 4 chars, range validated */}
                <div>
                  <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>Founded Year</label>
                  {editMode
                    ? <input className="inp" value={client.founded || ""} inputMode="numeric" maxLength={4}
                      onChange={e => { const v = e.target.value.replace(/\D/g, "").slice(0, 4); updateField("founded", v); }}
                      onKeyDown={e => {
                        if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
                        if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
                        if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                      }}
                      placeholder="e.g. 1998"
                      style={{ borderColor: client.founded && client.founded.length === 4 && (parseInt(client.founded) < 1800 || parseInt(client.founded) > new Date().getFullYear()) ? "#EF4444" : "" }} />
                    : <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{client.founded || "—"}</div>}
                </div>

                {/* Company Type — dropdown matching modal */}
                <div>
                  <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>Company Type</label>
                  {editMode
                    ? <select className="inp" value={client.companyType || ""} onChange={e => updateField("companyType", e.target.value)}>
                      <option value="">Select type</option>
                      {["Private Limited", "Public Limited", "LLP", "Partnership", "Sole Proprietorship", "Government", "NGO", "Other"].map(o => <option key={o}>{o}</option>)}
                    </select>
                    : <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{client.companyType || "—"}</div>}
                </div>

                {/* Employee Strength — dropdown matching modal */}
                <div>
                  <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>Employee Strength</label>
                  {editMode
                    ? <select className="inp" value={client.empRange || ""} onChange={e => updateField("empRange", e.target.value)}>
                      <option value="">Select range</option>
                      {["1-10", "11-50", "51-200", "201-500", "501-1000", "1000-5000", "5000+"].map(o => <option key={o}>{o}</option>)}
                    </select>
                    : <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{client.empRange || "—"}</div>}
                </div>

                {/* Revenue Range — dropdown matching modal */}
                <div>
                  <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>Annual Revenue Range (USD)</label>
                  {editMode
                    ? <select className="inp" value={client.revenueRange || ""} onChange={e => updateField("revenueRange", e.target.value)}>
                      <option value="">Select range</option>
                      {["Under $1M", "$1M–$10M", "$10M–$50M", "$50M–$200M", "$200M–$1B", "Above $1B"].map(o => <option key={o}>{o}</option>)}
                    </select>
                    : <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{client.revenueRange || "—"}</div>}
                </div>

              </div>
            )}
          </div>

          <div className="card" style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A" }}>Primary Contact</div>
              {pcEditMode ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setPcEditMode(false)}>Cancel</button>
                  <button className="btn-primary" style={{ fontSize: 12 }} onClick={() => { saveClient(); setPcEditMode(false); }}>Save Contact</button>
                </div>
              ) : (
                <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setPcEditMode(true)}>Edit</button>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              {[
                { label: "First Name", key: "firstName", inputType: "text", maxLen: CLIENT_FIELD_MAX.pc_first_name },
                { label: "Last Name", key: "lastName", inputType: "text", maxLen: CLIENT_FIELD_MAX.pc_last_name },
                { label: "Work Email", key: "email", inputType: "email", maxLen: CLIENT_FIELD_MAX.email },
                { label: "Direct Phone", key: "phone", inputType: "tel", maxLen: CLIENT_FIELD_MAX.pc_phone },
                { label: "Mobile", key: "mobile", inputType: "tel", maxLen: CLIENT_FIELD_MAX.pc_mobile },
                { label: "Designation", key: "designation", inputType: "text", maxLen: CLIENT_FIELD_MAX.pc_designation },
                { label: "Department", key: "department", inputType: "text", maxLen: CLIENT_FIELD_MAX.pc_department },
              ].map(({ label, key, inputType, maxLen }) => {
                const val = (client.primaryContact && client.primaryContact[key]) || "";
                const isPhone = inputType === "tel";
                const isEmail = inputType === "email";
                const emailOkPC = !val || EMAIL_REGEX.test(val);
                const phMap = { name: "Contact name", email: "contact@client.com", phone: "+91 44 xxxx xxxx", mobile: "+91 98xxx xxxxx", designation: "e.g. VP Operations", department: "e.g. Operations" };
                return (
                  <FieldRow key={key} label={label}>
                    {pcEditMode ? (
                      isPhone
                        ? <input className="inp" inputMode="tel" value={val} maxLength={maxLen}
                          onChange={e => updatePC(key, clampPhone(e.target.value, maxLen))}
                          onKeyDown={e => {
                            if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
                            if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
                            if (!/^[0-9+\-\s()]$/.test(e.key)) e.preventDefault();
                            if (/[0-9]/.test(e.key) && (val || "").replace(/\D/g, "").length >= 15) e.preventDefault();
                          }}
                          placeholder={phMap[key]} />
                        : <input className="inp" type={inputType} value={val} maxLength={maxLen}
                          onChange={e => updatePC(key, e.target.value.slice(0, maxLen))} placeholder={phMap[key]}
                          style={{ borderColor: isEmail && val && !emailOkPC ? "#EF4444" : "" }} />
                    ) : (
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{val || "—"}</div>
                    )}
                  </FieldRow>
                );
              })}
            </div>
          </div>

          {/* ── Communication card ── */}
          <div className="card" style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A" }}>Telephone, Fax & Shared Emails</div>
              {commEditMode
                ? <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setCommEditMode(false)}>Cancel</button>
                  <button className="btn-primary" style={{ fontSize: 12 }} onClick={() => { saveClient(); setCommEditMode(false); }}>Save</button>
                </div>
                : <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setCommEditMode(true)}>Edit</button>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              {/* Landline 1 — phone digits only */}
              {[
                { label: "Landline 1", key: "landline", ph: "+91 44 2222 0000", type: "phone" },
                { label: "Extn. 1", key: "landlineExtn", ph: "101", type: "extn" },
                { label: "Fax 1", key: "fax", ph: "+91 44 2222 0001", type: "phone" },
                { label: "Landline 2", key: "landline2", ph: "+91 44 3333 0000", type: "phone" },
                { label: "Extn. 2", key: "landline2Extn", ph: "202", type: "extn" },
                { label: "Fax 2", key: "fax2", ph: "+91 44 3333 0001", type: "phone" },
              ].map(({ label, key, ph, type }) => (
                <FieldRow key={key} label={label}>
                  {commEditMode ? (
                    type === "extn"
                      ? <input className="inp" inputMode="numeric" maxLength={CLIENT_FIELD_MAX.landlineExtn}
                        value={(client.communication && client.communication[key]) || ""}
                        onChange={e => updateComm(key, e.target.value.replace(/\D/g, "").slice(0, CLIENT_FIELD_MAX.landlineExtn))}
                        onKeyDown={e => {
                          if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
                          if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
                          if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                        }}
                        placeholder={ph} />
                      : <input className="inp" inputMode="tel" maxLength={CLIENT_FIELD_MAX.landline}
                        value={(client.communication && client.communication[key]) || ""}
                        onChange={e => updateComm(key, clampPhone(e.target.value, CLIENT_FIELD_MAX.landline))}
                        onKeyDown={e => {
                          if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
                          if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
                          if (!/^[0-9+\-\s()]$/.test(e.key)) e.preventDefault();
                          if (/[0-9]/.test(e.key) && ((client.communication && client.communication[key]) || "").replace(/\D/g, "").length >= 15) e.preventDefault();
                        }}
                        placeholder={ph} />
                  ) : (
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{(client.communication && client.communication[key]) || "—"}</div>
                  )}
                </FieldRow>
              ))}
            </div>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #F1F5F9", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { label: "Reception / General", key: "receptionEmail", ph: "info@client.com" },
                { label: "Finance / Accounts", key: "financeEmail", ph: "accounts@client.com" },
                { label: "HR / People", key: "hrEmail", ph: "hr@client.com" },
                { label: "Support / Helpdesk", key: "supportEmail", ph: "support@client.com" },
              ].map(({ label, key, ph }) => {
                const val = (client.communication && client.communication[key]) || "";
                const emailOkComm = !val || EMAIL_REGEX.test(val);
                return (
                  <FieldRow key={key} label={label}>
                    {commEditMode
                      ? <input className="inp" type="email" value={val} maxLength={CLIENT_FIELD_MAX.email}
                        onChange={e => updateComm(key, e.target.value.slice(0, CLIENT_FIELD_MAX.email))}
                        placeholder={ph}
                        style={{ borderColor: val && !emailOkComm ? "#EF4444" : "" }} />
                      : <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{val || "—"}</div>}
                  </FieldRow>
                );
              })}
            </div>
          </div>

          {/* ── Address card ── */}
          <div className="card" style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A" }}>Registered Office Address</div>
              {addrEditMode
                ? <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setAddrEditMode(false)}>Cancel</button>
                  <button className="btn-primary" style={{ fontSize: 12 }} onClick={() => { saveClient(); setAddrEditMode(false); }}>Save</button>
                </div>
                : <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setAddrEditMode(true)}>Edit</button>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <FieldRow label="Address Line 1">
                {addrEditMode
                  ? <input className="inp" value={(client.address && client.address.line1) || ""} maxLength={CLIENT_FIELD_MAX.addr_line} onChange={e => updateAddr("line1", e.target.value.slice(0, CLIENT_FIELD_MAX.addr_line))} placeholder="Building, street" />
                  : <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{(client.address && client.address.line1) || "—"}</div>}
              </FieldRow>
              <FieldRow label="Address Line 2">
                {addrEditMode
                  ? <input className="inp" value={(client.address && client.address.line2) || ""} maxLength={CLIENT_FIELD_MAX.addr_line} onChange={e => updateAddr("line2", e.target.value.slice(0, CLIENT_FIELD_MAX.addr_line))} placeholder="Area, landmark" />
                  : <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{(client.address && client.address.line2) || "—"}</div>}
              </FieldRow>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
              {[
                { label: "City", key: "city", inputType: "text", maxLen: CLIENT_FIELD_MAX.addr_city },
                { label: "State", key: "state", inputType: "text", maxLen: CLIENT_FIELD_MAX.addr_state },
                { label: "PIN/ZIP", key: "pin", inputType: "pin", maxLen: CLIENT_FIELD_MAX.addr_pin },
                { label: "Country", key: "country", inputType: "text", maxLen: CLIENT_FIELD_MAX.addr_country },
              ].map(({ label, key, inputType, maxLen }) => {
                const val = (client.address && client.address[key]) || "";
                const phMap = { city: "Chennai", state: "Tamil Nadu", pin: "600001", country: "India" };
                return (
                  <FieldRow key={key} label={label}>
                    {addrEditMode ? (
                      inputType === "pin"
                        ? <input className="inp" inputMode="numeric" maxLength={maxLen} value={val}
                          onChange={e => updateAddr(key, e.target.value.replace(/\D/g, "").slice(0, maxLen))}
                          onKeyDown={e => {
                            if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
                            if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
                            if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                          }}
                          placeholder={phMap[key]} />
                        : <input className="inp" value={val} maxLength={maxLen} onChange={e => updateAddr(key, e.target.value.slice(0, maxLen))} placeholder={phMap[key]} />
                    ) : (
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{val || "—"}</div>
                    )}
                  </FieldRow>
                );
              })}
            </div>
            {/* Billing address toggle */}
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #F1F5F9" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#0D1B2A" }}>Billing Address</div>
                {addrEditMode && (
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569", cursor: "pointer", marginLeft: 8 }}>
                    <input type="checkbox" checked={!!client.billingSame}
                      onChange={e => updateField("billingSame", e.target.checked)}
                      style={{ width: 14, height: 14, accentColor: "#E8520A" }} />
                    Same as office address
                  </label>
                )}
                {!addrEditMode && (
                  <span style={{ fontSize: 11, color: client.billingSame ? "#10B981" : "#64748B", background: client.billingSame ? "#F0FDF4" : "#F8FAFC", border: "1px solid " + (client.billingSame ? "#BBF7D0" : "#E2E8F0"), borderRadius: 100, padding: "2px 8px", fontWeight: 600 }}>
                    {client.billingSame ? "Same as office" : "Different address"}
                  </span>
                )}
              </div>
              {!client.billingSame && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <FieldRow label="Billing Line 1">
                      {addrEditMode
                        ? <input className="inp" value={(client.billingAddress && client.billingAddress.line1) || ""} maxLength={CLIENT_FIELD_MAX.addr_line} onChange={e => updateBilling("line1", e.target.value.slice(0, CLIENT_FIELD_MAX.addr_line))} placeholder="Building, street" />
                        : <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{(client.billingAddress && client.billingAddress.line1) || "—"}</div>}
                    </FieldRow>
                    <FieldRow label="Billing Line 2">
                      {addrEditMode
                        ? <input className="inp" value={(client.billingAddress && client.billingAddress.line2) || ""} maxLength={CLIENT_FIELD_MAX.addr_line} onChange={e => updateBilling("line2", e.target.value.slice(0, CLIENT_FIELD_MAX.addr_line))} placeholder="Area, landmark" />
                        : <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{(client.billingAddress && client.billingAddress.line2) || "—"}</div>}
                    </FieldRow>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
                    {[
                      { label: "City", key: "city", inputType: "text", maxLen: CLIENT_FIELD_MAX.addr_city },
                      { label: "State", key: "state", inputType: "text", maxLen: CLIENT_FIELD_MAX.addr_state },
                      { label: "PIN/ZIP", key: "pin", inputType: "pin", maxLen: CLIENT_FIELD_MAX.addr_pin },
                      { label: "Country", key: "country", inputType: "text", maxLen: CLIENT_FIELD_MAX.addr_country },
                    ].map(({ label, key, inputType, maxLen }) => {
                      const val = (client.billingAddress && client.billingAddress[key]) || "";
                      const phMap = { city: "Chennai", state: "Tamil Nadu", pin: "600001", country: "India" };
                      return (
                        <FieldRow key={key} label={label}>
                          {addrEditMode ? (
                            inputType === "pin"
                              ? <input className="inp" inputMode="numeric" maxLength={maxLen} value={val}
                                onChange={e => updateBilling(key, e.target.value.replace(/\D/g, "").slice(0, maxLen))}
                                onKeyDown={e => {
                                  if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
                                  if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
                                  if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                                }}
                                placeholder={phMap[key]} />
                              : <input className="inp" value={val} maxLength={maxLen} onChange={e => updateBilling(key, e.target.value.slice(0, maxLen))} placeholder={phMap[key]} />
                          ) : (
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{val || "—"}</div>
                          )}
                        </FieldRow>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Additional Contacts card ── */}
          <div className="card" style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A" }}>Additional Contacts</div>
                <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>Secondary contacts for Finance, HR, Operations etc.</div>
              </div>
              {contactsEditMode
                ? <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setContactsEditMode(false)}>Cancel</button>
                  <button className="btn-primary" style={{ fontSize: 12 }} onClick={saveContacts}>Save Contacts</button>
                </div>
                : <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setContactsEditMode(true)}>Edit</button>}
            </div>

            {(client.contacts || []).length === 0 && !contactsEditMode && (
              <div style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic", padding: "8px 0" }}>No additional contacts added.</div>
            )}

            {(client.contacts || []).map((ct, idx) => (
              <div key={ct.id} style={{ background: "#FAFBFF", border: "1.5px solid #E2E8F0", borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: contactsEditMode ? 12 : 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: "#8B5CF6", padding: "2px 8px", borderRadius: 100 }}>
                      {ct.type || "Operations"}
                    </span>
                    {!contactsEditMode && <span style={{ fontSize: 13, fontWeight: 700, color: "#0D1B2A" }}>{ct.name || [ct.firstName, ct.lastName].filter(Boolean).join(" ") || "—"}</span>}
                  </div>
                  {contactsEditMode && (
                    <button onClick={() => deleteContact(ct.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#EF4444", fontSize: 18, lineHeight: 1 }}>×</button>
                  )}
                </div>
                {contactsEditMode ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                    <FieldRow label="First Name">
                      <input className="inp" value={ct.firstName || ""} maxLength={CLIENT_FIELD_MAX.pc_first_name}
                        onChange={e => updateContact(ct.id, "firstName", e.target.value.slice(0, CLIENT_FIELD_MAX.pc_first_name))} placeholder="First name" />
                    </FieldRow>
                    <FieldRow label="Last Name">
                      <input className="inp" value={ct.lastName || ""} maxLength={CLIENT_FIELD_MAX.pc_last_name}
                        onChange={e => updateContact(ct.id, "lastName", e.target.value.slice(0, CLIENT_FIELD_MAX.pc_last_name))} placeholder="Last name" />
                    </FieldRow>
                    <FieldRow label="Type">
                      <select className="inp" value={ct.type || "Operations"} onChange={e => updateContact(ct.id, "type", e.target.value)}>
                        {["Operations", "Finance", "HR", "IT", "Legal", "Admin", "Other"].map(t => <option key={t}>{t}</option>)}
                      </select>
                    </FieldRow>
                    <FieldRow label="Designation">
                      <input className="inp" value={ct.designation || ""} maxLength={CLIENT_FIELD_MAX.pc_designation}
                        onChange={e => updateContact(ct.id, "designation", e.target.value.slice(0, CLIENT_FIELD_MAX.pc_designation))} placeholder="e.g. Finance Manager" />
                    </FieldRow>
                    <FieldRow label="Department">
                      <input className="inp" value={ct.department || ""} maxLength={CLIENT_FIELD_MAX.pc_department}
                        onChange={e => updateContact(ct.id, "department", e.target.value.slice(0, CLIENT_FIELD_MAX.pc_department))} placeholder="e.g. Finance" />
                    </FieldRow>
                    <FieldRow label="Email">
                      <input className="inp" type="email" value={ct.email || ""} maxLength={CLIENT_FIELD_MAX.email}
                        onChange={e => updateContact(ct.id, "email", e.target.value.slice(0, CLIENT_FIELD_MAX.email))} placeholder="email@client.com"
                        style={{ borderColor: ct.email && !EMAIL_REGEX.test(ct.email) ? "#EF4444" : "" }} />
                    </FieldRow>
                    <FieldRow label="Direct Phone">
                      <input className="inp" inputMode="tel" value={ct.phone || ""} maxLength={CLIENT_FIELD_MAX.pc_phone}
                        onChange={e => updateContact(ct.id, "phone", clampPhone(e.target.value, CLIENT_FIELD_MAX.pc_phone))}
                        onKeyDown={e => {
                          if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
                          if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
                          if (!/^[0-9+\-\s()]$/.test(e.key)) e.preventDefault();
                          if (/[0-9]/.test(e.key) && (ct.phone || "").replace(/\D/g, "").length >= 15) e.preventDefault();
                        }} placeholder="+91 44 xxxx xxxx" />
                    </FieldRow>
                    <FieldRow label="Mobile">
                      <input className="inp" inputMode="tel" value={ct.mobile || ""} maxLength={CLIENT_FIELD_MAX.pc_mobile}
                        onChange={e => updateContact(ct.id, "mobile", clampPhone(e.target.value, CLIENT_FIELD_MAX.pc_mobile))}
                        onKeyDown={e => {
                          if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
                          if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
                          if (!/^[0-9+\-\s()]$/.test(e.key)) e.preventDefault();
                          if (/[0-9]/.test(e.key) && (ct.mobile || "").replace(/\D/g, "").length >= 15) e.preventDefault();
                        }} placeholder="+91 9xxxx xxxxx" />
                    </FieldRow>
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    {[
                      ["Designation", ct.designation], ["Department", ct.department],
                      ["Email", ct.email], ["Phone", ct.phone], ["Mobile", ct.mobile],
                    ].map(([label, val]) => val ? (
                      <div key={label}>
                        <div style={{ fontSize: 10, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#1E293B" }}>{val}</div>
                      </div>
                    ) : null)}
                  </div>
                )}
              </div>
            ))}

            {contactsEditMode && (
              <button className="btn-secondary" style={{ fontSize: 12, width: "100%", justifyContent: "center", marginTop: 4 }} onClick={addNewContact}>
                + Add Contact
              </button>
            )}
          </div>

          {/* ── Internal Notes card ── */}
          <div className="card" style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A" }}>Internal Notes</div>
                <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>Not visible to the client. Max 200 characters.</div>
              </div>
              {notesEditMode
                ? <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setNotesEditMode(false)}>Cancel</button>
                  <button className="btn-primary" style={{ fontSize: 12 }} onClick={() => { saveClient(); setNotesEditMode(false); }}>Save</button>
                </div>
                : <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setNotesEditMode(true)}>Edit</button>}
            </div>
            {notesEditMode ? (
              <textarea className="inp" rows={5} value={client.notes || ""}
                maxLength={200}
                onChange={e => updateField("notes", e.target.value.slice(0, 200))}
                placeholder="Onboarding context, key accounts, SLAs, special arrangements…"
                style={{ resize: "vertical", fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6, width: "100%" }} />
            ) : (
              <div style={{ fontSize: 13, color: "#475569", whiteSpace: "pre-wrap", lineHeight: 1.6, fontStyle: client.notes ? "normal" : "italic" }}>
                {client.notes || "No notes yet."}
              </div>
            )}
          </div>

          <div className="card" style={{ padding: "20px 24px" }}>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A", marginBottom: 4 }}>Authorised Email Domains</div>
            <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 14 }}>Stakeholders must use these domains for OTP authentication</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
              {(client.domains || []).map(d => {
                const did = d.id || d;
                const isLast = (client.domains || []).length === 1;
                const isEditing = editingDomain && editingDomain.id === did;
                return (
                  <div key={did} style={{ display: "flex", alignItems: "center", gap: 6, background: "#EEF2FF", border: "1.5px solid #C7D2FE", borderRadius: 8, padding: "6px 12px" }}>
                    {isEditing ? (
                      <>
                        <input className="inp" value={editingDomain.value} maxLength={CLIENT_FIELD_MAX.domain}
                          autoFocus
                          onChange={e => setEditingDomain({ id: did, value: e.target.value.slice(0, CLIENT_FIELD_MAX.domain) })}
                          onKeyDown={e => {
                            if (e.key === "Enter") { editDomain(did, editingDomain.value); setEditingDomain(null); }
                            if (e.key === "Escape") setEditingDomain(null);
                          }}
                          style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, padding: "2px 6px", width: 200 }} />
                        <button onClick={() => { editDomain(did, editingDomain.value); setEditingDomain(null); }}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#10B981", fontSize: 14 }} title="Save">✓</button>
                        <button onClick={() => setEditingDomain(null)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 14 }} title="Cancel">✕</button>
                      </>
                    ) : (
                      <>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#4338CA", fontWeight: 600 }}>@{d.domain || d}</span>
                        <button onClick={() => setEditingDomain({ id: did, value: d.domain || d })}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#6366F1", fontSize: 12, padding: 0 }} title="Edit">✎</button>
                        <button onClick={() => remDomain(did)} disabled={isLast}
                          style={{ background: "none", border: "none", cursor: isLast ? "not-allowed" : "pointer", color: isLast ? "#E2E8F0" : "#A5B4FC", fontSize: 16, opacity: isLast ? 0.5 : 1 }}
                          title={isLast ? "At least one domain is required" : "Remove"}>&#215;</button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="inp" placeholder="new-domain.com" value={newDomain} onChange={e => setNewDomain(e.target.value)} onKeyDown={e => e.key === "Enter" && addDomain()} style={{ maxWidth: 240 }} />
              <button className="btn-secondary" onClick={addDomain} style={{ fontSize: 12 }}>+ Add Domain</button>
            </div>
          </div>

          <div className="card" style={{ padding: "20px 24px" }}>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A", marginBottom: 4 }}>Departments</div>
            <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 14 }}>Resources are allocated at department level</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
              {(client.departments || []).map(d => {
                const isLast = (client.departments || []).length === 1;
                const isEditing = editingDept && editingDept.id === d.id;
                return (
                  <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 7, background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: 8, padding: "7px 13px" }}>
                    {isEditing ? (
                      <>
                        <input className="inp" value={editingDept.value} maxLength={120}
                          autoFocus
                          onChange={e => setEditingDept({ id: d.id, value: e.target.value.slice(0, 120) })}
                          onKeyDown={e => {
                            if (e.key === "Enter") { editDept(d.id, editingDept.value); setEditingDept(null); }
                            if (e.key === "Escape") setEditingDept(null);
                          }}
                          style={{ fontSize: 12, padding: "2px 6px", width: 180 }} />
                        <button onClick={() => { editDept(d.id, editingDept.value); setEditingDept(null); }}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#10B981", fontSize: 14 }} title="Save">✓</button>
                        <button onClick={() => setEditingDept(null)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 14 }} title="Cancel">✕</button>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#1E293B" }}>{d.name}</span>
                        <button onClick={() => setEditingDept({ id: d.id, value: d.name })}
                          className="btn-ghost" style={{ padding: "0 0 0 4px", color: "#6366F1", fontSize: 12 }} title="Edit">✎</button>
                        <button onClick={() => !isLast && setConfirmDelDept({ id: d.id, name: d.name })}
                          disabled={isLast}
                          className="btn-ghost"
                          style={{ padding: "0 0 0 4px", color: isLast ? "#E2E8F0" : "#CBD5E1", fontSize: 16, cursor: isLast ? "not-allowed" : "pointer", opacity: isLast ? 0.5 : 1 }}
                          title={isLast ? "At least one department is required" : "Remove"}>&#215;</button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="inp" placeholder="New department name" value={newDept} onChange={e => setNewDept(e.target.value)} onKeyDown={e => e.key === "Enter" && addDept()} style={{ maxWidth: 260 }} />
              <button className="btn-secondary" onClick={addDept} style={{ fontSize: 12 }}>+ Add Dept</button>
            </div>
          </div>

          <div className="card" style={{ padding: "20px 24px" }}>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#0D1B2A", marginBottom: 4 }}>Stakeholders</div>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 16 }}>
              Stakeholders are assigned per department.
              <span style={{ background: "#D1FAE5", color: "#065F46", padding: "2px 6px", borderRadius: 100, fontWeight: 600, fontSize: 10, marginLeft: 8 }}>Active Review</span>
              = linked to an open review.
            </div>

            {(client.departments || []).length === 0 && (
              <div style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic", marginBottom: 16 }}>
                Add a department above to start adding stakeholders.
              </div>
            )}

            {(client.departments || []).map(dept => (
              <div key={dept.id} style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ background: "#EDE9FE", color: "#6D28D9", padding: "3px 10px", borderRadius: 100, fontWeight: 700, fontSize: 11 }}>Department</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#1E293B" }}>{dept.name}</span>
                  </div>
                  <button className="btn-secondary" style={{ fontSize: 11 }} onClick={() => addSH("dept", dept.id)}>+ Add</button>
                </div>
                <SHTable rows={deptSH(dept.id)} onUpdate={updateSH} onDelete={deleteSH} onConfirmDelete={(id, name) => setConfirmDelSH({ id, name })} onToggle={toggleSH} activeEmails={activeEmails} primaryStakeholderId={client.primaryStakeholderId} onSetPrimary={setPrimary} />
              </div>
            ))}

            {(client.stakeholders || []).length > 0 && (
              <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 10, borderTop: "1px solid #F1F5F9", marginTop: 8 }}>
                <button className="btn-primary" style={{ fontSize: 12 }} onClick={saveStakeholders}>Save Stakeholder Changes</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Remove Stakeholder Confirm Modal ── */}
      {confirmDelSH && (
        <div className="modal-overlay" style={{ left: 236 }}>
          <div className="modal" style={{ maxWidth: 420, width: "min(420px, calc(100% - 32px))" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "28px 32px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>&#128465;</div>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#0D1B2A" }}>Remove Stakeholder?</div>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>{confirmDelSH.name}</div>
                </div>
              </div>
              <div style={{ background: "#FFF5F0", border: "1.5px solid #FDBA74", borderRadius: 10, padding: "14px 16px", marginBottom: 20, fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>
                ⚠ This stakeholder will be deactivated and unlinked from this client. Active reviews assigned to them cannot be modified.
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setConfirmDelSH(null)}>Cancel</button>
                <button style={{ flex: 1, background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                  onClick={() => { deleteSH(confirmDelSH.id); setConfirmDelSH(null); }}>
                  Yes, Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (() => {
        const deptCount = (client.departments || []).length;
        const shCount = (client.stakeholders || []).length;
        const domainCount = (client.domains || []).length;
        return (
          <div className="modal-overlay" style={{ left: 236 }}>
            <div className="modal" style={{ maxWidth: 460, width: "min(460px, calc(100% - 32px))" }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: "28px 32px" }}>
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>&#128465;</div>
                  <div>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#0D1B2A" }}>Deactivate {client.name}?</div>
                    <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>{client.code} &middot; {client.industry}</div>
                  </div>
                </div>

                {/* What will be affected */}
                <div style={{ background: "#FFF5F0", border: "1.5px solid #FDBA74", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#92400E", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.6 }}>The client will be marked Inactive</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    {[
                      { icon: "🏢", label: "Departments", count: deptCount },
                      { icon: "👥", label: "Stakeholders", count: shCount },
                      { icon: "🌐", label: "Domains", count: domainCount },
                    ].map(({ icon, label, count }) => (
                      <div key={label} style={{ background: "#fff", borderRadius: 8, padding: "10px 12px", textAlign: "center", border: "1px solid #FED7AA" }}>
                        <div style={{ fontSize: 18, marginBottom: 4 }}>{icon}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: count > 0 ? "#DC2626" : "#94A3B8" }}>{count}</div>
                        <div style={{ fontSize: 10, color: "#94A3B8", fontWeight: 600 }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#64748B", marginBottom: 22, lineHeight: 1.6 }}>
                  <strong>Note:</strong> The client record stays in the database — status flips to <strong>Inactive</strong>. You can re-activate them by editing the status. Employee allocations linked to this client must be updated separately.
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                  <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setConfirmDel(false)}>Cancel</button>
                  <button style={{ flex: 1, background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer" }} onClick={deleteClient}>
                    Yes, Deactivate
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {confirmDelDept && (() => {
        const shCount = (client.stakeholders || []).filter(s => s.deptId === confirmDelDept.id).length;
        return (
          <div className="modal-overlay" style={{ left: 236 }}>
            <div className="modal" style={{ maxWidth: 420, width: "min(420px, calc(100% - 32px))" }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: "26px 28px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>&#128465;</div>
                  <div>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: "#0D1B2A" }}>Remove "{confirmDelDept.name}"?</div>
                    <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>Department of {client.name}</div>
                  </div>
                </div>

                <div style={{ background: "#FFF5F0", border: "1.5px solid #FDBA74", borderRadius: 9, padding: "12px 14px", marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#92400E", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6 }}>What will be affected</div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <div style={{ flex: 1, background: "#fff", borderRadius: 7, padding: "10px", textAlign: "center", border: "1px solid #FED7AA" }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: shCount > 0 ? "#DC2626" : "#94A3B8" }}>{shCount}</div>
                      <div style={{ fontSize: 10, color: "#94A3B8", fontWeight: 600 }}>Dept Stakeholders</div>
                    </div>
                    <div style={{ flex: 1, background: "#fff", borderRadius: 7, padding: "10px", textAlign: "center", border: "1px solid #FED7AA" }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#DC2626" }}>1</div>
                      <div style={{ fontSize: 10, color: "#94A3B8", fontWeight: 600 }}>Department</div>
                    </div>
                  </div>
                </div>

                {shCount > 0 && (
                  <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#991B1B", marginBottom: 16 }}>
                    <strong>{shCount} stakeholder{shCount > 1 ? "s" : ""}</strong> assigned to this department will also be removed. Employee allocations to this department must be reassigned manually.
                  </div>
                )}

                <div style={{ display: "flex", gap: 10 }}>
                  <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setConfirmDelDept(null)}>Cancel</button>
                  <button style={{ flex: 1, background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                    onClick={() => { remDept(confirmDelDept.id); setConfirmDelDept(null); }}>
                    Yes, Remove Department
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {confirmDelDomain && (() => {
        const affectedSH = (client.stakeholders || []).filter(s => {
          const d = s.email?.toLowerCase().split("@")[1];
          return d === confirmDelDomain.domain.toLowerCase();
        });
        return (
          <div className="modal-overlay" style={{ left: 236 }}>
            <div className="modal" style={{ maxWidth: 440, width: "min(440px, calc(100% - 32px))" }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: "26px 28px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: "#EEF2FF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>&#127758;</div>
                  <div>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: "#0D1B2A" }}>Remove Domain?</div>
                    <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 13, color: "#4338CA", marginTop: 3, fontWeight: 700 }}>@{confirmDelDomain.domain}</div>
                  </div>
                </div>
                <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "11px 14px", fontSize: 12, color: "#991B1B", marginBottom: affectedSH.length ? 12 : 20, lineHeight: 1.6 }}>
                  Stakeholders using <strong>@{confirmDelDomain.domain}</strong> will no longer be able to authenticate via OTP for <strong>{client.name}</strong>. This cannot be undone.
                </div>
                {affectedSH.length > 0 && (
                  <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "11px 14px", fontSize: 12, color: "#92400E", marginBottom: 20, lineHeight: 1.7 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠️ {affectedSH.length} stakeholder{affectedSH.length !== 1 ? "s" : ""} {affectedSH.length === 1 ? "uses" : "use"} this domain and will lose OTP access:</div>
                    {affectedSH.map(s => (
                      <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                        <span style={{ fontWeight: 600 }}>{[s.firstName, s.lastName].filter(Boolean).join(" ") || s.name || s.id}</span>
                        <span style={{ color: "#B45309" }}>—</span>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{s.email}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setConfirmDelDomain(null)}>Cancel</button>
                  <button style={{ flex: 1, background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                    onClick={() => doRemDomain(confirmDelDomain.id)}>
                    Yes, Remove Domain
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showAdd && <AddClientModal onAdd={addClient} onClose={() => setShowAdd(false)} visible={showAdd} existingClients={clients} />}
      {showImport && <ClientImportModal onClose={() => { setShowImport(false); setImportFile(null); setImportResult(null); }} importFile={importFile} setImportFile={setImportFile} onImport={runClientImport} loading={importLoading} result={importResult} onDownloadTemplate={downloadClientTemplate} templateLoading={templateLoading} />}
      {toast && <Toast msg={toast} type={toastType} />}
    </div>
  );
};

export default ClientConfig;